import { trfEventDate } from './trfTime';
import { formatPreset } from '@/i18n/intl';

export type SectionKey = 'intake' | 'patient' | 'household' | 'finances' | 'travel';

export type FieldType = 'text' | 'textarea' | 'date' | 'number' | 'checkbox' | 'select' | 'multi' | 'file';

/**
 * D-FB-12: what a `file` field stores as its answer — a reference to the
 * `SourceDocument` the upload route created, never the bytes. `filename` is
 * denormalized so a row can name the file without a second fetch; it is
 * immutable once uploaded, so it cannot drift from the document.
 */
export interface FileAnswer {
  source_document_id: string;
  filename: string;
}

/**
 * D-FB-11: ask this question only when another answer matches.
 *
 * Declared HERE, on the renderer contract, rather than only on the definition
 * shape it comes from — #1639 was exactly the property existing on the
 * definition and having nowhere to land on the way to the family. `field_id`
 * names a question by its id (which is its dotted storage key); `values` are
 * the driver answers that reveal this one. Evaluated by `isRevealed`
 * (lib/formDefinitions.ts) — the ONE interpretation the builder preview and the
 * wizard share; never re-implement it at a call site.
 */
export interface VisibleWhen {
  field_id: string;
  values: string[];
}

export interface FieldSpec {
  // Dotted path within the section, e.g. "primary_contact.phone" → household.primary_contact.phone
  key: string;
  label: string;
  type: FieldType;
  options?: { value: string; label: string }[];
  // Every option value → label a family could EVER have chosen for this field,
  // retired ones included (#1011). `options` above is the live set — what a
  // picker renders — and retiring an option must remove it from that set. But
  // retiring a question was never allowed to hide a family's answer from the
  // staff deciding on their application, and retiring an OPTION is the same
  // promise one level down: without this map, the oldest answers are exactly
  // the ones that degrade to a raw slug on screen.
  optionLabels?: Record<string, string>;
  placeholder?: string;
  // Mandatory at submit-step advance (2027 workbook match, PR-1 policy: every
  // workbook question required EXCEPT the secondary-contact block and live-only
  // additive fields pending #961).
  //
  // #1112 (DECISIONS 2026-09-07) removed "conditional follow-ups" from that
  // exception list. A follow-up MAY now be required, but only together with
  // `visibleWhen`: required-and-ungated would demand an answer from a family
  // the question was never asked of, on a page they cannot leave. The pairing
  // is what makes it safe, and `intakeFields.otherSpecify.test.ts` pins it. The wizard
  // blocks step-advance on empty required fields; the backend submit floor
  // (applications.js: family last name, child first name, diagnosis) is
  // unchanged and remains the server-side minimum.
  required?: boolean;
  // Small hint rendered under the label (workbook helper text).
  helper?: string;
  // Numeric sanity bounds (QA fix wave 2026-08-20): enforced as input attrs
  // AND at step-advance (fieldValueProblem). Money/count fields carry min 0/1;
  // the grant ask carries max 1000 (the server clamps at materialization —
  // this keeps the CLIENT honest about its own stated cap).
  min?: number;
  max?: number;
  // Named format check (QA fix wave): serializable by NAME so the coming
  // form-definition model can carry it; implementations live in FIELD_FORMATS.
  format?: 'email' | 'phone' | 'zip';
  // 'dropdown' renders a native <select> (with legacy out-of-list value
  // preservation) instead of the default pill buttons — for long option sets
  // where the workbook literally instructs "Select one from the dropdown menu".
  display?: 'dropdown';
  // Options that depend on a SIBLING field's value within the same section
  // (F-3: the region field is a US-state dropdown when country=US and a
  // province/territory dropdown when country=CA). `on` is the sibling field
  // key; `byValue` maps that field's value → the option list to show. When a
  // stored value isn't in the resolved list (legacy free-text), the input
  // preserves and renders it so no data is silently dropped on edit.
  conditionalOptions?: {
    on: string;
    byValue: Record<string, { value: string; label: string }[]>;
    /** #602: the SECTION the driver lives in, when it is not a sibling.
     *  `country` is an `intake` question and `ethnicity` a `patient` one, so
     *  the ethnicity taxonomy cannot be resolved from section-local values the
     *  way the region list can. Naming the section here is what lets a renderer
     *  reach it — see `resolveConditionalDriver`, which every renderer must use
     *  instead of reading `siblingValues[on]` directly. Absent means "sibling",
     *  which is what `residence_state` still is. */
    onSection?: SectionKey;
    /** B-403: how the driver field is LABELLED on screen, for the "pick X
     *  first" placeholder. `on` is a storage key (`country`) and may not match
     *  the question's wording (the 2027 workbook calls it "Region") — a
     *  placeholder built from the key sends a stuck applicant hunting for a
     *  question that does not exist. Renderers must use this, never `on`. */
    onLabel?: string;
  };
  // #583: the LABEL may depend on the same kind of SIBLING field value the
  // option list already does. Resolve with `resolveFieldLabel(field,
  // siblingValues)` — never read `field.label` directly in a renderer that has
  // siblings on hand.
  conditionalLabel?: {
    on: string;
    byValue: Record<string, string>;
    /** #2523: the SECTION the driver lives in, when it is not a sibling —
     *  exactly `conditionalOptions.onSection`. The household, finances and
     *  travel questions word themselves for Canada off `intake.country`, which
     *  is another section's answer. Resolve through `resolveFieldLabel(field,
     *  siblingValues, sectionValues)`; absent means "sibling", which is what
     *  `residence_state` still is. */
    onSection?: SectionKey;
  };
  // F-039: renders a small group subheading immediately BEFORE this field.
  // Kept for the staff drawer / detail renderers; the /apply wizard now groups
  // via APPLICATION_SCREENS instead.
  groupLabel?: string;
  // #1838: a date question's own bound and its comparison against another date
  // question — the wider half of #1111, which shipped three rules hardcoded on
  // two seeded field ids and left the schema unable to express any of them.
  // Compared at end-of-day UTC. Evaluated by `lib/dateRules.ts` on the client
  // and `services/dateRules.js` on the server, never by a renderer.
  dateBound?: { direction: 'past' | 'future'; message?: string };
  dateCompare?: {
    op: 'not_before' | 'not_after';
    section_id: string;
    field_id: string;
    message?: string;
  };
  // D-FB-11 (#1639): ask this question only while another answer matches.
  // Distinct from `conditionalOptions`, which changes WHICH ANSWERS a question
  // offers — this decides whether the question is asked at all. A renderer must
  // resolve it through `isRevealed` (lib/formDefinitions.ts), never by reading
  // the driver's value itself: a dangling or retired driver reveals the field,
  // and that fail-open posture is the same one the server's submit gate takes.
  visibleWhen?: VisibleWhen;
  // R21 (2026-10-02, spec 2026-09-29-committee-one-pager), widened 2026-10-02
  // (QA duplicate-line fix, "Switches, on by default" — Mario in-session):
  // this question's answer is a line somewhere on the committee one-pager —
  // the child card, the facts column, or the right panel's Treatment line.
  // Mirrors the published-version property of the same name
  // (`backend/src/models/FormDefinitionVersion.js` `assertCommitteeFlags`) —
  // a builder publish carries it straight through (D-FB immutability rules
  // do not apply to it; it is free to flip either way after publish). EVERY
  // source field behind a one-pager line (`backend/src/services/committeeOnePager/content.js`
  // `DECK_LINES`) seeds it `true` (below) — the deck's own defaults becoming
  // REAL, visible switches on day one, instead of a hidden code list nothing
  // in the builder shows. A field composing a MULTI-source line (DOB,
  // Diagnosis, Stage, Location, Parent Information) never also gets a
  // `committee_label` — that line's label is fixed; only a single-source
  // line's field does. `intakeFields.test.ts` pins the full set.
  committee_one_pager?: boolean;
  // The short label the one-pager prints under (e.g. "Household Size"
  // rather than the full question). Falls back to `label` when absent.
  committee_label?: string;
}

// F-3 — US state dropdown (50 states + DC; no territories per the 2026-07-09
// Clean-Six build defaults, OQ3). Values are USPS 2-letter codes so legacy
// free-text like "NJ" still matches on edit.
export const US_STATE_OPTIONS = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' }, { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' }, { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' }, { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' }, { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' }, { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' }, { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' }, { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' }, { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

// F-3 — Canada province/territory dropdown (the 13 provinces + territories per
// the Clean-Six build defaults). Values are the Canada Post 2-letter codes.
export const CA_PROVINCE_OPTIONS = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon' },
];

// 2027 workbook order + phrasing. Values unchanged where the ethnicity was
// already captured (stored data keeps reading); `middle_eastern` is live-only
// (absent from the workbook) and stays pending the #961 ratification.
export const ETHNICITY_OPTIONS = [
  { value: 'native_american', label: 'American Indian or Alaska Native' },
  { value: 'asian', label: 'Asian' },
  { value: 'black_or_african_american', label: 'Black or African American' },
  { value: 'hispanic_or_latino', label: 'Hispanic or Latino' },
  { value: 'pacific_islander', label: 'Native Hawaiian or Pacific Islander' },
  { value: 'white', label: 'White' },
  { value: 'multi_racial', label: 'Multiple Races' },
  { value: 'middle_eastern', label: 'Middle Eastern or North African' },
  { value: 'prefer_not_to_say', label: 'Prefer Not to Answer' },
  { value: 'other', label: 'Other' },
];

// #602 (decision G1, option A) — the Canada track asks the race/ethnicity
// question the way Canadian institutions ask it. Statistics Canada splits this
// across two census questions — Indigenous identity (First Nations / Métis /
// Inuk (Inuit)) and population group — and TRF asks ONE question, so the two
// are merged into a single list in StatCan's own order and wording.
//
// Four values are DELIBERATELY shared with ETHNICITY_OPTIONS, with byte-identical
// labels: `white`, `multi_racial`, `prefer_not_to_say`, `other`. Both taxonomies
// flatten into the one "Patient Ethnicity" export column, whose label map folds
// every conditional branch last-entry-wins (formDefinitions.toFieldSpec,
// admin-families.js, committeePacket.js, familyFilter.js) — divergent labels for
// one value would make the column depend on fold order rather than the answer.
// Pinned by intakeFields.test.ts.
export const CA_ETHNICITY_OPTIONS = [
  { value: 'first_nations', label: 'First Nations' },
  { value: 'inuit', label: 'Inuk (Inuit)' },
  { value: 'metis', label: 'Métis' },
  { value: 'south_asian', label: 'South Asian' },
  { value: 'chinese', label: 'Chinese' },
  { value: 'black', label: 'Black' },
  { value: 'filipino', label: 'Filipino' },
  { value: 'arab', label: 'Arab' },
  { value: 'latin_american', label: 'Latin American' },
  { value: 'southeast_asian', label: 'Southeast Asian' },
  { value: 'west_asian', label: 'West Asian' },
  { value: 'korean', label: 'Korean' },
  { value: 'japanese', label: 'Japanese' },
  { value: 'white', label: 'White' },
  { value: 'multi_racial', label: 'Multiple Races' },
  { value: 'prefer_not_to_say', label: 'Prefer Not to Answer' },
  { value: 'other', label: 'Other' },
];

export const TREATMENT_OPTIONS = [
  { value: 'chemotherapy', label: 'Chemotherapy' },
  { value: 'radiation', label: 'Radiation' },
  { value: 'surgery', label: 'Surgery' },
  { value: 'immunotherapy', label: 'Immunotherapy' },
  { value: 'stem_cell_transplant', label: 'Stem cell transplant' },
  { value: 'targeted_therapy', label: 'Targeted therapy' },
  { value: 'clinical_trial', label: 'Clinical trial' },
  { value: 'cart_cell', label: 'CAR T-cell' },
  { value: 'bone_graft', label: 'Bone graft' },
  { value: 'tumor_resection', label: 'Tumor resection' },
  { value: 'maintenance_chemo', label: 'Maintenance chemo' },
  { value: 'blood_platelet_transfusions', label: 'Blood & platelet transfusions' },
  { value: 'dialysis', label: 'Dialysis' },
  { value: 'other', label: 'Other' },
];

// Legacy travel-spend buckets — field retired by the 2027 match (replaced by
// TRAVEL_SPEND_BANDS below, whose boundaries differ). Export kept so the
// retired field still renders stored values on staff surfaces.
export const SPEND_BUCKETS = [
  { value: 'bucket_0_250', label: '$0 – $250' },
  { value: 'bucket_250_500', label: '$250 – $500' },
  { value: 'bucket_500_1000', label: '$500 – $1,000' },
  { value: 'bucket_1000_plus', label: '$1,000+' },
  { value: 'not_applicable', label: 'Not applicable' },
];

// ── 2027 workbook option sets (Application - Draft 2027, Ana 2026-07-02) ──────

const YES_NO = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const YES_NO_UNSURE = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'unsure', label: 'Unsure' },
];

export const SCHOOL_STATUS_OPTIONS = [
  { value: 'full_time', label: 'Attending school full-time' },
  { value: 'part_time', label: 'Attending school part-time or intermittently' },
  { value: 'not_attending_treatment', label: 'Not currently attending school due to medical treatment' },
  { value: 'homeschool_hospital', label: 'Homeschooled or receiving hospital/homebound instruction' },
  { value: 'not_school_aged', label: 'Not school-aged' },
  { value: 'prefer_not_to_say', label: 'Prefer not to answer' },
];

export const CANCER_TYPE_OPTIONS = [
  { value: 'leukemia', label: 'Leukemia' },
  { value: 'lymphoma', label: 'Lymphoma' },
  { value: 'brain_tumor', label: 'Brain Tumor' },
  { value: 'neuroblastoma', label: 'Neuroblastoma' },
  { value: 'wilms_tumor', label: 'Wilms Tumor' },
  { value: 'sarcoma', label: 'Sarcoma' },
  { value: 'retinoblastoma', label: 'Retinoblastoma' },
  { value: 'hepatoblastoma', label: 'Hepatoblastoma' },
  { value: 'germ_cell_tumor', label: 'Germ Cell Tumor' },
  { value: 'other', label: 'Other (please specify)' },
];

export const STAGE_RISK_OPTIONS = [
  { value: 'low_risk', label: 'Low Risk' },
  { value: 'standard_risk', label: 'Standard Risk' },
  { value: 'intermediate_risk', label: 'Intermediate Risk' },
  { value: 'high_risk', label: 'High Risk' },
  { value: 'stage_1', label: 'Stage I' },
  { value: 'stage_2', label: 'Stage II' },
  { value: 'stage_3', label: 'Stage III' },
  { value: 'stage_4', label: 'Stage IV' },
  { value: 'not_applicable', label: 'Not Applicable' },
  { value: 'unknown', label: 'Unknown' },
];

// #1613 split the one "stage or risk classification" question in two, published
// as application v5 on production 2026-09-05. Staging and risk are different
// axes: a leukaemia protocol has a risk group and no stage, a solid tumour has a
// stage, and asking for both in one dropdown made every family pick the half
// that applied to them and drop the other. The retired original keeps its
// options above so stored answers still render.
export const RISK_CLASSIFICATION_OPTIONS = [
  { value: 'low_risk', label: 'Low Risk' },
  { value: 'standard_risk', label: 'Standard Risk' },
  { value: 'intermediate_risk', label: 'Intermediate Risk' },
  { value: 'high_risk', label: 'High Risk' },
  { value: 'not_applicable', label: 'Not Applicable' },
  { value: 'unknown', label: 'Unknown' },
];

// Roman-numeral VALUES here, where the retired `stage_risk` used `stage_1`.
// They are different fields, so the stored vocabularies do not have to agree
// and must not be "harmonised" — production already holds answers under both.
export const CANCER_STAGE_OPTIONS = [
  { value: 'stage_i', label: 'Stage I' },
  { value: 'stage_ii', label: 'Stage II' },
  { value: 'stage_iii', label: 'Stage III' },
  { value: 'stage_iv', label: 'Stage IV' },
  { value: 'not_applicable', label: 'Not Applicable' },
  { value: 'unknown', label: 'Unknown' },
];

export const TREATMENT_STATUS_OPTIONS = [
  { value: 'newly_diagnosed', label: 'Newly Diagnosed' },
  { value: 'active_treatment', label: 'Active Treatment' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'survivorship', label: 'Survivorship' },
  { value: 'relapse', label: 'Relapse' },
  { value: 'palliative_care', label: 'Palliative Care' },
  { value: 'other', label: 'Other (please specify)' },
];

// #1615 — treatment BURDEN, not treatment severity. TRF funds non-medical
// costs, so what the grant committee needs from the medical section is how much
// a diagnosis disrupts a household week by week: a twice-daily protocol and a
// monthly one are the same `treatment_status` and a different life. Both fields
// carry a `not_in_treatment` escape for the survivorship case, the same way
// `stage_risk` carries "Not Applicable" — a required question with no honest
// answer is how a form teaches families to guess.
export const TREATMENT_FREQUENCY_OPTIONS = [
  { value: 'daily_or_more', label: 'Daily or more than once a day' },
  { value: 'several_times_week', label: 'Several times a week' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly_to_triweekly', label: 'Every 2\u20133 weeks' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'less_than_monthly', label: 'Less often than monthly' },
  { value: 'varies_by_cycle', label: 'Varies by treatment cycle' },
  { value: 'not_in_treatment', label: 'Not currently in treatment' },
];

export const TREATMENT_SETTING_OPTIONS = [
  { value: 'inpatient', label: 'Inpatient \u2014 staying at the hospital' },
  { value: 'outpatient', label: 'Outpatient \u2014 visits from home' },
  { value: 'both', label: 'Both, depending on the phase' },
  { value: 'not_in_treatment', label: 'Not currently in treatment' },
];

export const INSURANCE_COVERAGE_OPTIONS = [
  { value: 'private_employer', label: 'Private/Employer-Sponsored Insurance' },
  { value: 'medicaid', label: 'Medicaid' },
  { value: 'chip', label: "Children’s Health Insurance Program (CHIP)" },
  { value: 'tricare', label: 'Military Insurance (TRICARE)' },
  { value: 'medicare', label: 'Medicare' },
  { value: 'multiple_plans', label: 'Multiple Insurance Plans' },
  { value: 'uninsured', label: 'Uninsured' },
  { value: 'other', label: 'Other (please specify)' },
];

// #2399 (D-2399, "ship now") — the Canada track's coverage list, transcribed
// from the 2027 workbook's CANADA tab, cell D22. Provincial/territorial plans
// cover the hospital care itself, so the workbook asks about SUPPLEMENTAL or
// extended coverage. Values shared with INSURANCE_COVERAGE_OPTIONS carry
// byte-identical labels for the same reason CA_ETHNICITY_OPTIONS' do: every
// export folds both lists into one label map, last entry wins. That is why the
// cell's "Private / Employer-Sponsored Insurance" keeps the US spelling, and
// why `other` stays `other` — `insurance_coverage_other` reveals on exactly it.
export const CA_INSURANCE_COVERAGE_OPTIONS = [
  { value: 'private_employer', label: 'Private/Employer-Sponsored Insurance' },
  { value: 'provincial_supplemental', label: 'Provincial / Territorial Supplemental Plan' },
  { value: 'federal_plan', label: 'Federal Government Plan' },
  { value: 'multiple_plans', label: 'Multiple Insurance Plans' },
  { value: 'no_supplemental', label: 'No Supplemental Coverage / Self-Pay' },
  { value: 'other', label: 'Other (please specify)' },
];

export const INSURANCE_PROVIDER_OPTIONS = [
  { value: 'aetna', label: 'Aetna' },
  { value: 'anthem_bcbs', label: 'Anthem Blue Cross Blue Shield' },
  { value: 'bcbs', label: 'Blue Cross Blue Shield' },
  { value: 'cigna', label: 'Cigna' },
  { value: 'health_net', label: 'Health Net' },
  { value: 'humana', label: 'Humana' },
  { value: 'kaiser', label: 'Kaiser Permanente' },
  { value: 'medicaid', label: 'Medicaid' },
  { value: 'medicare', label: 'Medicare' },
  { value: 'tricare', label: 'TRICARE' },
  { value: 'unitedhealthcare', label: 'UnitedHealthcare' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to answer' },
];

// #2399 — the Canada track's carriers, from the CANADA tab, cell D23. The
// cell's "Other (please specify in the question below)" is stored as the
// shared `other` with the shared label: `insurance_provider_other` (#1113)
// reveals on exactly that value, and is the question below.
export const CA_INSURANCE_PROVIDER_OPTIONS = [
  { value: 'sun_life', label: 'Sun Life Financial' },
  { value: 'canada_life', label: 'Canada Life' },
  { value: 'manulife', label: 'Manulife' },
  { value: 'desjardins', label: 'Desjardins Insurance' },
  { value: 'beneva', label: 'Beneva' },
  { value: 'greenshield', label: 'GreenShield Canada' },
  { value: 'blue_cross_canada', label: 'Blue Cross' },
  { value: 'empire_life', label: 'Empire Life' },
  { value: 'ia_financial', label: 'iA Financial Group (Industrial Alliance)' },
  { value: 'provincial_program', label: 'Provincial/Territorial Government Program' },
  { value: 'federal_program', label: 'Federal Government Program' },
  { value: 'not_applicable', label: 'Not Applicable / No Supplemental Provider' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to answer' },
];

export const COSTS_NOT_COVERED_OPTIONS = [
  { value: 'travel_lodging', label: 'Travel/lodging' },
  { value: 'copays_deductibles', label: 'Co-pays/deductibles' },
  { value: 'prescriptions', label: 'Prescription medications' },
  { value: 'medical_equipment', label: 'Durable medical equipment' },
  { value: 'meals_during_travel', label: 'Meals/food during treatment travel' },
  { value: 'lost_wages', label: 'Lost wages' },
  { value: 'none', label: 'None' },
  { value: 'other', label: 'Other' },
];

export const RELATIONSHIP_OPTIONS = [
  { value: 'mother', label: 'Mother' },
  { value: 'father', label: 'Father' },
  { value: 'legal_guardian', label: 'Legal Guardian' },
  { value: 'grandparent', label: 'Grandparent' },
  { value: 'other', label: 'Other' },
];

// #2523 (D-2523): one list for every country — the platform's own five
// languages, then Other. French is Canada's second official language, and a
// family reading the form in Portuguese or Chinese could not say so. New values
// are additive: `english`, `spanish` and `other` keep their stored meaning. The
// values are exactly what the server's option mint makes of these labels
// (`slugifyKey`), so the live form's publish lands the same values.
export const PRIMARY_LANGUAGE_OPTIONS = [
  { value: 'english', label: 'English' },
  { value: 'spanish', label: 'Spanish' },
  { value: 'french', label: 'French' },
  { value: 'portuguese', label: 'Portuguese' },
  { value: 'chinese', label: 'Chinese' },
  { value: 'other', label: 'Other' },
];

export const MILITARY_SERVICE_OPTIONS = [
  { value: 'active_duty', label: 'Yes, currently active duty' },
  { value: 'veteran', label: 'Yes, veteran' },
  { value: 'guard_reserves', label: 'Yes, National Guard/Reserves' },
  { value: 'no', label: 'No' },
  { value: 'prefer_not_to_say', label: 'Prefer not to answer' },
];

// #2523 — the Canada track. The National Guard is a US institution; the
// Canadian Armed Forces' part-time component is the Reserve Force. A new value
// rather than a relabelled `guard_reserves`, for the reason #2399's lists give:
// every export folds both branches into one label map, so a shared value must
// carry one label. The other four answers are shared, word for word.
export const CA_MILITARY_SERVICE_OPTIONS = MILITARY_SERVICE_OPTIONS.map((option) => (
  option.value === 'guard_reserves' ? { value: 'reserve_force', label: 'Yes, Reserve Force' } : option
));

export const INCOME_BAND_OPTIONS = [
  { value: 'lt_25k', label: 'Less than $25,000' },
  { value: '25k_49k', label: '$25,000 – $49,999' },
  { value: '50k_74k', label: '$50,000 – $74,999' },
  { value: '75k_99k', label: '$75,000 – $99,999' },
  { value: '100k_149k', label: '$100,000 – $149,999' },
  { value: '150k_plus', label: '$150,000+' },
];

// #2523 — the same bands, written in Canadian dollars. New values (`cad_…`):
// a shared value must carry one label across both branches (see
// CA_MILITARY_SERVICE_OPTIONS). The thresholds are the US numbers as written;
// TRF has not set Canadian bands.
export const CA_INCOME_BAND_OPTIONS = [
  { value: 'cad_lt_25k', label: 'Less than CA$25,000' },
  { value: 'cad_25k_49k', label: 'CA$25,000 – CA$49,999' },
  { value: 'cad_50k_74k', label: 'CA$50,000 – CA$74,999' },
  { value: 'cad_75k_99k', label: 'CA$75,000 – CA$99,999' },
  { value: 'cad_100k_149k', label: 'CA$100,000 – CA$149,999' },
  { value: 'cad_150k_plus', label: 'CA$150,000+' },
];

export const SUPPORT_RECEIVED_OPTIONS = [
  { value: 'social_security', label: 'Social Security (SSI/SSDI)' },
  { value: 'child_support', label: 'Child Support' },
  { value: 'alimony', label: 'Alimony' },
  { value: 'unemployment', label: 'Unemployment Benefits' },
  { value: 'tanf_welfare', label: 'TANF/Welfare' },
  { value: 'snap', label: 'SNAP (Food Stamps)' },
  { value: 'paid_family_leave', label: 'Paid Family Leave (FMLA wage replacement)' },
  { value: 'none', label: 'None' },
  { value: 'other', label: 'Other' },
];

// #2399 — the Canadian benefits. The localizer drafted them in column C of the
// US tab (C51) and they never moved to the CANADA tab (memo 2026-08-11 §2).
// Each cell entry carries a "(Replaces <US programme>)" crosswalk note for the
// workbook's editors; the labels keep the programme names and drop those notes,
// keeping only the examples a family can recognise themselves in. `none` and
// `other` are not in the cell and are carried from the US list unchanged: this
// is a required checklist, and a family on no support must be able to say so.
export const CA_SUPPORT_RECEIVED_OPTIONS = [
  { value: 'cpp_disability_oas', label: 'Canada Pension Plan (CPP) Disability Benefits / Old Age Security (OAS)' },
  { value: 'child_support_special_expenses', label: 'Child Support / Special Expenses Payments' },
  { value: 'spousal_support', label: 'Spousal Support / Alimony' },
  { value: 'employment_insurance', label: 'Employment Insurance (EI) Benefits / Provincial Training Allowances' },
  { value: 'provincial_social_assistance', label: 'Provincial or Territorial Social Assistance (e.g., Ontario Works, Alberta AISH, BC Income Assistance)' },
  { value: 'provincial_food_programs', label: 'Provincial Food Programs / Food Banks' },
  { value: 'caregiving_benefits', label: 'Maternity, Parental, or Caregiving Benefits' },
  { value: 'none', label: 'None' },
  { value: 'other', label: 'Other' },
];

export const EMPLOYMENT_IMPACT_OPTIONS = [
  { value: 'unpaid_time_off', label: 'Took unpaid time off work' },
  { value: 'reduced_hours', label: 'Reduced work hours' },
  { value: 'fmla_leave', label: 'Used FMLA or protected leave' },
  { value: 'extended_leave', label: 'Went on extended leave of absence' },
  { value: 'resigned', label: 'Had to resign/quit job' },
  { value: 'lost_job', label: 'Lost job/employment ended (layoff/termination)' },
  { value: 'new_job', label: 'Started new or different job due to schedule/financial needs' },
  { value: 'other', label: 'Other' },
  { value: 'no_change', label: 'Does not apply, no change' },
];

// #2399 — the workbook has no Canadian employment cell, so the Canada track is
// the US list with its one American term made neutral: FMLA is a US statute,
// and every province has its own job-protected leave. A new value rather than a
// relabelled `fmla_leave`, because the export folds both lists into one label
// map and a shared value must carry one label.
export const CA_EMPLOYMENT_IMPACT_OPTIONS = EMPLOYMENT_IMPACT_OPTIONS.map((option) => (
  option.value === 'fmla_leave' ? { value: 'protected_leave', label: 'Used protected leave' } : option
));

export const TIME_OFF_WHO_OPTIONS = [
  { value: 'primary_lt_1mo', label: 'Primary caregiver – Less than 1 month' },
  { value: 'primary_1_3mo', label: 'Primary caregiver – 1–3 months' },
  { value: 'primary_gt_3mo', label: 'Primary caregiver – More than 3 months' },
  { value: 'secondary_lt_1mo', label: 'Secondary caregiver – Less than 1 month' },
  { value: 'secondary_1_3mo', label: 'Secondary caregiver – 1–3 months' },
  { value: 'secondary_gt_3mo', label: 'Secondary caregiver – More than 3 months' },
  { value: 'no_change', label: 'Does not apply, no change' },
];

export const STABILITY_OPTIONS = [
  { value: 'very_stable', label: 'Very stable' },
  { value: 'somewhat_stable', label: 'Somewhat stable' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'somewhat_unstable', label: 'Somewhat unstable' },
  { value: 'very_unstable', label: 'Very unstable' },
];

export const STRESSOR_OPTIONS = [
  { value: 'loss_of_income', label: 'Loss of income' },
  { value: 'uncovered_medical', label: 'Medical expenses not covered by insurance' },
  { value: 'housing_insecurity', label: 'Housing insecurity' },
  { value: 'transport_travel', label: 'Transportation/travel costs' },
  { value: 'food_insecurity', label: 'Food insecurity' },
  { value: 'childcare', label: 'Childcare' },
  { value: 'other', label: 'Other' },
];

export const TRAVEL_TYPE_OPTIONS = [
  { value: 'local_only', label: 'Local travel only' },
  { value: 'out_of_city', label: 'Out-of-city travel' },
  { value: 'out_of_state', label: 'Out-of-state travel' },
  { value: 'air_travel', label: 'Air travel required' },
  { value: 'extended_stays', label: 'Extended stays near treatment center' },
  { value: 'not_applicable', label: 'Not applicable' },
];

// #2523 — "out-of-state" is a US term; a Canadian family crosses a province.
export const CA_TRAVEL_TYPE_OPTIONS = TRAVEL_TYPE_OPTIONS.map((option) => (
  option.value === 'out_of_state' ? { value: 'out_of_province', label: 'Out-of-province travel' } : option
));

export const MILES_BAND_OPTIONS = [
  { value: 'lt_50', label: 'Less than 50 miles' },
  { value: '50_199', label: '50–199 miles' },
  { value: '200_499', label: '200–499 miles' },
  { value: '500_999', label: '500–999 miles' },
  { value: '1000_plus', label: '1,000+ miles' },
  { value: 'not_applicable', label: 'Not applicable' },
];

// #2523 — the Canada track measures in kilometres. Each band is the US band's
// mile boundary converted (50 mi ≈ 80 km, 200 ≈ 320, 500 ≈ 800, 1,000 ≈ 1,600)
// and rounded, so the two tracks still read as the same distances. New values
// (`km_…`); `not_applicable` is shared.
export const CA_KM_BAND_OPTIONS = [
  { value: 'km_lt_80', label: 'Less than 80 km' },
  { value: 'km_80_319', label: '80–319 km' },
  { value: 'km_320_799', label: '320–799 km' },
  { value: 'km_800_1599', label: '800–1,599 km' },
  { value: 'km_1600_plus', label: '1,600+ km' },
  { value: 'not_applicable', label: 'Not applicable' },
];

export const TRAVEL_SPEND_BAND_OPTIONS = [
  { value: '0', label: '$0' },
  { value: '1_250', label: '$1 – $250' },
  { value: '251_500', label: '$251 – $500' },
  { value: '501_1000', label: '$501 – $1,000' },
  { value: '1001_2500', label: '$1,001 – $2,500' },
  { value: '2500_plus', label: '$2,500+' },
  { value: 'unsure', label: 'Unsure' },
  { value: 'not_applicable', label: 'Not applicable' },
];

// #2523 — the same spend bands in Canadian dollars. New values (`cad_…`);
// `unsure` and `not_applicable` are shared.
export const CA_TRAVEL_SPEND_BAND_OPTIONS = [
  { value: 'cad_0', label: 'CA$0' },
  { value: 'cad_1_250', label: 'CA$1 – CA$250' },
  { value: 'cad_251_500', label: 'CA$251 – CA$500' },
  { value: 'cad_501_1000', label: 'CA$501 – CA$1,000' },
  { value: 'cad_1001_2500', label: 'CA$1,001 – CA$2,500' },
  { value: 'cad_2500_plus', label: 'CA$2,500+' },
  { value: 'unsure', label: 'Unsure' },
  { value: 'not_applicable', label: 'Not applicable' },
];

export const RELOCATED_OPTIONS = [
  { value: 'yes_temporarily', label: 'Yes, temporarily' },
  { value: 'yes_permanently', label: 'Yes, permanently' },
  { value: 'no', label: 'No' },
];

export const BURDEN_EXPENSE_OPTIONS = [
  { value: 'gas_fuel', label: 'Gas/fuel' },
  { value: 'airfare', label: 'Airfare' },
  { value: 'lodging_hotels', label: 'Lodging/hotels' },
  { value: 'meals_traveling', label: 'Meals while traveling' },
  { value: 'parking_tolls', label: 'Parking/tolls' },
  { value: 'rental_transport', label: 'Rental car/transportation services' },
  { value: 'lost_wages_travel', label: 'Lost wages due to travel' },
  { value: 'other', label: 'Other' },
];

export const OVERNIGHT_STAYS_OPTIONS = [
  { value: 'yes_frequently', label: 'Yes, frequently' },
  { value: 'yes_occasionally', label: 'Yes, occasionally' },
  { value: 'no', label: 'No' },
];

export const STAYS_6MO_OPTIONS = [
  { value: '1_2', label: '1–2' },
  { value: '3_5', label: '3–5' },
  { value: '6_10', label: '6–10' },
  { value: 'more_than_10', label: 'More than 10' },
  { value: 'ongoing', label: 'Ongoing/long-term hospitalization' },
  { value: 'not_applicable', label: 'Not applicable' },
];

export const STAY_LENGTH_OPTIONS = [
  { value: 'lt_3_days', label: 'Less than 3 days' },
  { value: '3_7_days', label: '3–7 days' },
  { value: '1_2_weeks', label: '1–2 weeks' },
  { value: 'gt_2_weeks', label: 'More than 2 weeks' },
  { value: 'varies', label: 'Varies significantly' },
  { value: 'not_applicable', label: 'Not applicable' },
];

export const EXTENDED_AWAY_OPTIONS = [
  { value: '1_2_weeks', label: 'Yes, 1–2 weeks' },
  { value: '3_4_weeks', label: 'Yes, 3–4 weeks' },
  { value: '6_10_weeks', label: 'Yes, 6–10 weeks' },
  { value: 'gt_10_weeks', label: 'Yes, more than 10 weeks' },
  { value: 'not_applicable', label: 'Not applicable' },
];

export const GRANT_PRIORITY_OPTIONS = [
  { value: 'rent_mortgage', label: 'Rent or mortgage' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'lodging_near_treatment', label: 'Lodging near treatment center' },
  { value: 'food_basic_needs', label: 'Food & basic needs' },
  { value: 'childcare', label: 'Childcare' },
  { value: 'insurance_costs', label: 'Insurance-related costs' },
  { value: 'other', label: 'Other' },
];

export const ASSISTANCE_IMPACT_OPTIONS = [
  { value: 'prevent_eviction', label: 'Prevent eviction or housing instability' },
  { value: 'reduce_stress', label: 'Reduce financial stress significantly' },
  { value: 'caregiver_focus', label: 'Allow caregiver to focus more on child’s care' },
  { value: 'cover_travel', label: 'Help cover travel to treatment' },
  { value: 'basic_needs', label: 'Ensure access to basic needs (food, utilities)' },
  { value: 'other', label: 'Other' },
];

export const HOW_HEARD_OPTIONS = [
  { value: 'hospital_social_worker', label: 'Hospital Social Worker' },
  { value: 'child_life_specialist', label: 'Child Life Specialist' },
  { value: 'healthcare_provider', label: 'Healthcare Provider' },
  { value: 'another_family', label: 'Another Family' },
  { value: 'online_search', label: 'Online Search' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'other', label: 'Other' },
];

export const IMMEDIATE_IMPACT_OPTIONS = [
  { value: 'prevent_eviction', label: 'Prevent eviction or housing loss' },
  { value: 'keep_utilities', label: 'Keep utilities active' },
  { value: 'continued_travel', label: 'Allow continued travel to treatment' },
  { value: 'reduce_work_loss_stress', label: 'Reduce caregiver work loss stress' },
  { value: 'reduce_debt_reliance', label: 'Reduce reliance on debt/credit' },
  { value: 'basic_needs', label: 'Ensure basic needs are met' },
  { value: 'other', label: 'Other' },
];

export const WITHOUT_ASSISTANCE_OPTIONS = [
  { value: 'take_on_debt', label: 'Take on debt' },
  { value: 'miss_delay_travel', label: 'Miss or delay treatment travel' },
  { value: 'fall_behind_housing', label: 'Fall behind on housing/utilities' },
  { value: 'reduce_essentials', label: 'Reduce essential expenses (food, care)' },
  { value: 'rely_on_support', label: 'Rely on family/friends support' },
  { value: 'other', label: 'Other' },
];

// ── Live field catalog, keyed by STORAGE section ──────────────────────────────
// 2027 workbook match (PR-1). Shape changes never retype a live key (spec
// D-FB-10): a changed-shape question gets a NEW key and the old field moves to
// RETIRED_SECTION_FIELDS below (stored data keeps rendering on staff surfaces).

// #2523 (D-2523) — the household, finances and travel questions that spoke
// only American: the military question, dollar amounts, miles and
// "out-of-state". Each follows `intake.country` the way #2399's lists do,
// with two shapes:
//  - `canadaLabel(...)` words the QUESTION for Canada. Only a CA branch: a US
//    family (and a family that has not answered Region) reads `label`, so the
//    live US wording and its translations stay exactly as they are.
//  - `canadaOptions(...)` swaps the ANSWER list, `options` staying the US list
//    as the fallback and the required-gate short-circuit (see `ethnicity`).
// Stored keys never change: a Canadian income still lands in
// `finances.monthly_income_usd`, and only the words around it move.
function canadaLabel(ca: string): NonNullable<FieldSpec['conditionalLabel']> {
  return { on: 'country', onSection: 'intake', byValue: { CA: ca } };
}
function canadaOptions(
  us: { value: string; label: string }[],
  ca: { value: string; label: string }[],
): NonNullable<FieldSpec['conditionalOptions']> {
  return { on: 'country', onSection: 'intake', onLabel: 'Region', byValue: { US: us, CA: ca } };
}

export const SECTION_FIELDS: Record<SectionKey, FieldSpec[]> = {
  intake: [
    { key: 'application_type', label: 'Application type', type: 'select', required: true, options: [
      { value: 'new', label: 'New applicant (Year 1)' },
      { value: 'renewal_year_2', label: 'Renewal — Year 2' },
      { value: 'renewal_year_3', label: 'Renewal — Year 3' },
    ] },
    { key: 'applied_at', label: 'Date of application', type: 'date' },
    { key: 'family_last_name', label: 'Family last name', type: 'text', required: true },
    // F-3: country comes FIRST so it can drive the region dropdown below.
    // 2027 workbook top matter calls this "Region".
    { key: 'country', label: 'Region', type: 'select', required: true, options: [
      { value: 'US', label: 'United States' },
      { value: 'CA', label: 'Canada' },
    ] },
    // F-3/#583: address field, not a gate (D-3, DECISIONS 2026-08-10). Renders
    // on the Contact screen's address block per the workbook.
    // 2026-10-02: a Location composite SOURCE (committeeDeck.ts) — composite
    // members never get a committee_label (the line's own label, "Location",
    // is fixed).
    { key: 'residence_state', label: 'State / province of residence', type: 'select', required: true, committee_one_pager: true, conditionalOptions: {
      on: 'country',
      // Matches this field's on-screen label above (`country` → "Region").
      onLabel: 'Region',
      byValue: { US: US_STATE_OPTIONS, CA: CA_PROVINCE_OPTIONS },
    }, conditionalLabel: {
      on: 'country',
      byValue: { US: 'State of residence', CA: 'Province / territory of residence' },
    } },
    // 2027 Mission & Impact section.
    { key: 'how_heard', label: 'How did you learn about the Tyler Robinson Foundation?', type: 'select', required: true, display: 'dropdown', options: HOW_HEARD_OPTIONS },
    { key: 'immediate_impact', label: 'What would be the most immediate impact of receiving TRF assistance?', type: 'select', required: true, display: 'dropdown', options: IMMEDIATE_IMPACT_OPTIONS },
    { key: 'without_assistance', label: 'If TRF assistance were not available, what would your family most likely need to do?', type: 'select', required: true, display: 'dropdown', options: WITHOUT_ASSISTANCE_OPTIONS },
    // Live-only (absent from the 2027 workbook) — kept pending #961; AP-09
    // (prior-tragedy retention) is an explicit open TRF decision.
    { key: 'family_story', label: 'Family story / bio', type: 'textarea' },
    { key: 'prior_hardship.flag', label: 'Has there been prior tragedy in the family?', type: 'checkbox' },
    { key: 'prior_hardship.notes', label: 'If yes, brief description', type: 'textarea' },
  ],
  patient: [
    { key: 'first_name', label: 'First name', type: 'text', required: true },
    { key: 'last_name', label: 'Last name', type: 'text', required: true },
    // 2026-10-02: DOB composite SOURCES (committeeDeck.ts) — composite
    // members never get a committee_label (the line's own label, "DOB" or,
    // gender-alone, "Gender", is fixed).
    { key: 'date_of_birth', label: 'Date of birth', type: 'date', required: true, committee_one_pager: true },
    { key: 'gender', label: 'Child’s gender', type: 'select', required: true, committee_one_pager: true, options: [
      { value: 'female', label: 'Female' }, { value: 'male', label: 'Male' },
      { value: 'non_binary', label: 'Non-binary' }, { value: 'other', label: 'Other' },
      { value: 'prefer_not_to_say', label: 'Prefer not to say' },
    ] },
    // #1112 — asked only when the driver is "Other", and required once it is.
    // Both halves of the ruling: an application could be submitted with
    // `gender: other` and nothing after it, so staff read "Other: (nothing)";
    // and the empty box rendered for every family whatever they picked.
    // `visibleWhen` (D-FB-11) is resolved by `isRevealed` on BOTH sides, and
    // the server's `missingRequiredFields` skips an unrevealed field — so a
    // hidden-but-required question can never strand a family.
    { key: 'gender_other', label: 'If other, specify', type: 'text', required: true,
      visibleWhen: { field_id: 'gender', values: ['other'] } },
    // #602 — the taxonomy follows the applicant's country. `options` stays the
    // US list on purpose and is NOT dead code: `hasAnythingToPick` short-circuits
    // on live static options (so the required gate needs no change on either
    // side of the stack), the renderer falls back to it when the driver cannot
    // be resolved (so the control is never dead), and the builder's publish
    // preflight reads it as answerable. The driver is cross-section — see
    // `conditionalOptions.onSection`.
    { key: 'ethnicity', label: 'Race/Ethnicity', type: 'multi', required: true, options: ETHNICITY_OPTIONS, conditionalOptions: {
      on: 'country',
      onSection: 'intake',
      // Matches the driver's on-screen label (the 2027 workbook calls it "Region").
      onLabel: 'Region',
      byValue: { US: ETHNICITY_OPTIONS, CA: CA_ETHNICITY_OPTIONS },
    } },
    // Free-text diagnosis stays (the workbook asks BOTH free-text Diagnosis and
    // the controlled cancer-type dropdown — PRD FC-19); server submit floor.
    // The label + helper are NOT the workbook's bare "Diagnosis" on purpose
    // (walk 2026-08-21): in the WIZARD this question and `cancer_type` sit one
    // screen apart with this one first, so a bare label reads as "the dropdown
    // is missing". The workbook's order and question set are unchanged — only
    // the wording that tells the reader which question is which.
    // The helper deliberately names NO position ("next screen", "below"):
    // SECTION_FIELDS is shared with the staff FamilyIntakeDrawer, which renders
    // the whole `patient` section on one tab — there the two fields are
    // ADJACENT, so any positional phrasing would be false on that surface.
    // Pinned by intakeFields.test.ts.
    // Staff-facing labels for the same key (admin-families.js CSV_FIELDS,
    // services/reportColumns.js) are deliberately separate strings and stay
    // "Diagnosis" — do not "fix" that drift; the audiences want different words.
    // 2026-10-02: a Diagnosis composite SOURCE (committeeDeck.ts; the
    // fallback chain is cancer_type -> cancer_type_other -> diagnosis) —
    // composite members never get a committee_label (the line's own label,
    // "Diagnosis", is fixed).
    {
      key: 'diagnosis',
      label: 'Diagnosis, in your own words',
      helper: 'The specific cancer type is a separate question.',
      type: 'text',
      required: true,
      committee_one_pager: true,
    },
    // 2026-10-02: a single-source deck line (committeeDeck.ts) — premarked ON
    // with the deck's own label, honoured exactly like the 7 facts.
    { key: 'diagnosed_at', label: 'Date of diagnosis', type: 'date', required: true, committee_one_pager: true, committee_label: 'Diagnosis Date' },
    // 2026-10-02: a Diagnosis composite SOURCE — see `diagnosis` above.
    { key: 'cancer_type', label: 'What type of cancer has your child been diagnosed with?', type: 'select', required: true, display: 'dropdown', options: CANCER_TYPE_OPTIONS, committee_one_pager: true },
    // #1112 — asked only when the driver is "Other", and required once it is.
    // Both halves of the ruling: an application could be submitted with
    // `cancer_type: other` and nothing after it, so staff read "Other: (nothing)";
    // and the empty box rendered for every family whatever they picked.
    // `visibleWhen` (D-FB-11) is resolved by `isRevealed` on BOTH sides, and
    // the server's `missingRequiredFields` skips an unrevealed field — so a
    // hidden-but-required question can never strand a family.
    // 2026-10-02: a Diagnosis composite SOURCE — see `diagnosis` above.
    { key: 'cancer_type_other', label: 'If other, please specify', type: 'text', required: true, committee_one_pager: true,
      visibleWhen: { field_id: 'cancer_type', values: ['other'] } },
    // #1613 / #1704: `stage_risk` is retired below, replaced by these two.
    // Neither is required — a family whose diagnosis has one axis and not the
    // other must be able to leave the other alone, which is the whole reason
    // the combined question was split.
    // Curly apostrophe, matching every other label here and the #707 grammar
    // ratchet. Production's published v5 labels use straight ones — a cosmetic
    // divergence recorded in #1865, not worth republishing the form over.
    // 2026-10-02: Stage composite SOURCES (committeeDeck.ts) — composite
    // members never get a committee_label (the line's own label, "Stage", is
    // fixed). The retired `stage_risk` below is NOT a switchable source — it
    // stays the Stage line's legacy fallback in content.js, read only when
    // neither of these two has any value at all.
    { key: 'risk_classification', label: 'What is your child\u2019s current risk classification?', type: 'select', display: 'dropdown', options: RISK_CLASSIFICATION_OPTIONS, committee_one_pager: true },
    { key: 'stage', label: 'What is your child\u2019s current cancer stage?', type: 'select', display: 'dropdown', options: CANCER_STAGE_OPTIONS, committee_one_pager: true },
    { key: 'is_relapse', label: 'Has your child experienced a relapse or recurrence of cancer?', type: 'checkbox' },
    { key: 'treatment_status', label: 'What is your child’s current treatment status?', type: 'select', required: true, display: 'dropdown', options: TREATMENT_STATUS_OPTIONS },
    // #1112 — asked only when the driver is "Other", and required once it is.
    // Both halves of the ruling: an application could be submitted with
    // `treatment_status: other` and nothing after it, so staff read "Other: (nothing)";
    // and the empty box rendered for every family whatever they picked.
    // `visibleWhen` (D-FB-11) is resolved by `isRevealed` on BOTH sides, and
    // the server's `missingRequiredFields` skips an unrevealed field — so a
    // hidden-but-required question can never strand a family.
    { key: 'treatment_status_other', label: 'If other, please specify', type: 'text', required: true,
      visibleWhen: { field_id: 'treatment_status', values: ['other'] } },
    { key: 'treatment_frequency', label: 'How often does your child have treatment or clinic visits?', type: 'select', required: true, display: 'dropdown', options: TREATMENT_FREQUENCY_OPTIONS },
    { key: 'treatment_setting', label: 'Is your child treated as an inpatient or an outpatient?', type: 'select', required: true, display: 'dropdown', options: TREATMENT_SETTING_OPTIONS },
    // #2399 — this question, the provider below, and `support_received` and
    // `employment_impact` in finances follow the applicant's country exactly
    // the way `ethnicity` does (#602): `options` stays the US list (the
    // fallback and the required-gate short-circuit on both sides of the
    // stack), and the driver is cross-section, so `onSection` names it.
    { key: 'insurance_coverage', label: 'What type of health insurance coverage does the patient currently have?', type: 'select', required: true, display: 'dropdown', options: INSURANCE_COVERAGE_OPTIONS, conditionalOptions: {
      on: 'country',
      onSection: 'intake',
      onLabel: 'Region',
      byValue: { US: INSURANCE_COVERAGE_OPTIONS, CA: CA_INSURANCE_COVERAGE_OPTIONS },
    } },
    // #1112 — asked only when the driver is "Other", and required once it is.
    // Both halves of the ruling: an application could be submitted with
    // `insurance_coverage: other` and nothing after it, so staff read "Other: (nothing)";
    // and the empty box rendered for every family whatever they picked.
    // `visibleWhen` (D-FB-11) is resolved by `isRevealed` on BOTH sides, and
    // the server's `missingRequiredFields` skips an unrevealed field — so a
    // hidden-but-required question can never strand a family.
    { key: 'insurance_coverage_other', label: 'If other, please specify', type: 'text', required: true,
      visibleWhen: { field_id: 'insurance_coverage', values: ['other'] } },
    { key: 'insurance_provider', label: 'What is the patient’s current health insurance provider?', type: 'select', required: true, display: 'dropdown', options: INSURANCE_PROVIDER_OPTIONS, conditionalOptions: {
      on: 'country',
      onSection: 'intake',
      onLabel: 'Region',
      byValue: { US: INSURANCE_PROVIDER_OPTIONS, CA: CA_INSURANCE_PROVIDER_OPTIONS },
    } },
    // #1113 (A4) — matches the three siblings above; a family on a small
    // regional insurer can now name it instead of stopping at "Other".
    // #1112 — asked only when the driver is "Other", and required once it is.
    // Both halves of the ruling: an application could be submitted with
    // `insurance_provider: other` and nothing after it, so staff read "Other: (nothing)";
    // and the empty box rendered for every family whatever they picked.
    // `visibleWhen` (D-FB-11) is resolved by `isRevealed` on BOTH sides, and
    // the server's `missingRequiredFields` skips an unrevealed field — so a
    // hidden-but-required question can never strand a family.
    { key: 'insurance_provider_other', label: 'If other, please specify', type: 'text', required: true,
      visibleWhen: { field_id: 'insurance_provider', values: ['other'] } },
    { key: 'costs_not_covered', label: 'What costs are NOT covered by insurance?', type: 'multi', required: true, helper: 'Select all that apply', options: COSTS_NOT_COVERED_OPTIONS },
    { key: 'deductible_met', label: 'Have you met your annual deductible?', type: 'select', required: true, options: YES_NO_UNSURE },
    // R21: one of the 7 deck-default fields — premarked ON so the committee
    // one-pager's facts column keeps printing what the deck always printed,
    // as a real switch a TRFer can see and turn off (spec 2026-09-29-committee-one-pager).
    { key: 'hospital', label: 'Hospital(s) where the child receives treatment', type: 'text', required: true, committee_one_pager: true, committee_label: 'Hospital' },
    { key: 'school_status', label: 'If your child is school-aged, what does their current schooling look like?', type: 'select', required: true, display: 'dropdown', options: SCHOOL_STATUS_OPTIONS },
    // Live-only — kept pending #961 (treatments capture is clinically useful;
    // an explicit keep/drop decision belongs to the ratification session).
    // 2026-10-02: the right panel's single-source deck line (committeeDeck.ts)
    // — premarked ON with the deck's own label "Treatment" (not this
    // question's own label, "Treatment types"). The QA finding this whole
    // change fixes: before, flagging this question ALSO printed it in the
    // facts column as an extra, duplicating the panel's always-on Treatment
    // line.
    { key: 'treatments', label: 'Treatment types', type: 'multi', options: TREATMENT_OPTIONS, committee_one_pager: true, committee_label: 'Treatment' },
    // 2026-10-02: a single-source deck line — premarked ON with the deck's
    // own label, honoured exactly like the 7 facts.
    { key: 'treatment_ended_at', label: 'End of treatment (if applicable)', type: 'date', committee_one_pager: true, committee_label: 'End of Treatment' },
    { key: 'referring_social_worker', label: 'Referring social worker', type: 'text' },
    // 2026-10-11 (J1 c06): a Canadian family names a province or territory.
    { key: 'treatment_location', label: 'City / state of treatment', type: 'text',
      conditionalLabel: canadaLabel('City / province or territory of treatment') },
  ],
  household: [
    // F-039: role-neutral wording; keys unchanged (mother_full_name /
    // father_full_name) so backend CSV export + legacy data are unaffected.
    // 2027 restructures to primary guardian + relationship; AP-61's
    // parenthetical is gone.
    // 2026-10-02: a Parent Information composite SOURCE (committeeDeck.ts) —
    // composite members never get a committee_label (the line's own label,
    // "Parent Information", is fixed).
    { key: 'mother_full_name', label: 'Primary parent/guardian full name', type: 'text', required: true, groupLabel: 'Primary contact', committee_one_pager: true },
    { key: 'primary_contact.relationship', label: 'Relationship to patient', type: 'select', required: true, display: 'dropdown', options: RELATIONSHIP_OPTIONS },
    { key: 'primary_contact.email', label: 'Primary email address', type: 'text', required: true, format: 'email' },
    { key: 'primary_contact.phone', label: 'Primary phone number', type: 'text', required: true, format: 'phone' },
    // 2027 address split (replaces the retired mailing_address textarea —
    // discrete fields feed the home-state reports; D-FB-10 new keys).
    { key: 'address.street', label: 'Street address', type: 'text', required: true, groupLabel: 'Home address' },
    { key: 'address.apt', label: 'Apartment/unit number (if applicable)', type: 'text' },
    // 2026-10-02: Location composite SOURCES (committeeDeck.ts) — composite
    // members never get a committee_label (the line's own label, "Location",
    // is fixed).
    { key: 'address.city', label: 'City', type: 'text', required: true, committee_one_pager: true },
    { key: 'address.zip', label: 'Zip / postal code', type: 'text', required: true, format: 'zip', committee_one_pager: true },
    { key: 'primary_language', label: 'Primary language spoken at home', type: 'select', required: true, options: PRIMARY_LANGUAGE_OPTIONS },
    // Secondary contact block — optional as a block (not every family has one).
    // 2026-10-02: a Parent Information composite SOURCE — see
    // `mother_full_name` above.
    { key: 'father_full_name', label: 'Secondary contact full name', type: 'text', groupLabel: 'Secondary contact', committee_one_pager: true },
    { key: 'secondary_contact.relationship', label: 'Secondary contact — relationship to patient', type: 'select', display: 'dropdown', options: RELATIONSHIP_OPTIONS },
    { key: 'secondary_contact.phone', label: 'Secondary contact phone number', type: 'text', format: 'phone' },
    { key: 'secondary_contact.email', label: 'Secondary contact email address', type: 'text', format: 'email' },
    { key: 'secondary_contact.authorized', label: 'Is the secondary contact authorized to discuss and make decisions regarding this application on behalf of the family?', type: 'select', options: YES_NO },
    { key: 'single_parent', label: 'Is this a single-parent household?', type: 'select', required: true, options: YES_NO, groupLabel: 'Your household' },
    // R21: deck default (see `hospital` above for the rule).
    { key: 'size', label: 'How many people currently live in your household (including the patient)?', type: 'number', required: true, min: 1, max: 30, committee_one_pager: true, committee_label: 'Household Size' },
    // #2701: the question asked for a COUNT while its box and helper asked for
    // names and ages, so families answered "2" and staff lost the names. The
    // question now asks for the list; still a required textarea.
    { key: 'siblings', label: 'List the patient’s siblings (names and ages)', type: 'textarea', required: true, helper: 'Do not include the patient. If there are none, write “None”.' },
    // #2523 — Canadian Armed Forces wording and a Reserve Force answer for CA.
    { key: 'military_service', label: 'Is anyone in the household currently serving in the U.S. Armed Forces or a military veteran?', type: 'select', required: true, display: 'dropdown', options: MILITARY_SERVICE_OPTIONS,
      conditionalLabel: canadaLabel('Is anyone in the household currently serving in the Canadian Armed Forces or a military veteran?'),
      conditionalOptions: canadaOptions(MILITARY_SERVICE_OPTIONS, CA_MILITARY_SERVICE_OPTIONS) },
    // Live-only — kept pending #961.
    { key: 'members', label: 'Everyone living in the home (names, birthdays, ages, relationship to patient)', type: 'textarea' },
    { key: 'child_lives_with', label: 'Child lives with', type: 'multi', options: [
      { value: 'mother', label: 'Mother' }, { value: 'father', label: 'Father' },
      { value: 'both_parents', label: 'Both parents' }, { value: 'grandparent', label: 'Grandparent' },
      { value: 'other_guardian', label: 'Other guardian' },
      { value: 'foster_care', label: 'Foster care' },
    ] },
    // Published on production as application v6 (2026-09-28, a TRFer in the
    // form builder: "Added parent guardian DOB on 9/27/2026") and carried
    // back by #2282. Labels are production's words, trailing space included:
    // a translation attaches only to the exact live English (sha256), so a
    // trimmed copy here left production's es/pt/fr/zh unattached.
    { key: 'primary_parent_guardian_dob', label: 'Primary Parent Guardian DOB? ', type: 'date' },
    { key: 'secondary_parent_guardian_dob', label: 'Secondary Parent Guardian DOB? ', type: 'date' },
  ],
  finances: [
    // 2027: income captured as bands (08-19 call ruling — "de la manera que
    // está en el excel"). Exact-USD fields retired below (D-FB-10).
    // R21: deck default (see `hospital` above for the rule). 2026-10-06
    // (Victor's deck-template rulings): the yearly band is now the FALLBACK
    // half of the one-pager's composite "Monthly Income" line, so it carries
    // no committee_label of its own.
    // #2523 — Canadian-dollar bands for CA.
    { key: 'income_band', label: 'What is your total household annual income?', type: 'select', required: true, display: 'dropdown', options: INCOME_BAND_OPTIONS, committee_one_pager: true,
      conditionalOptions: canadaOptions(INCOME_BAND_OPTIONS, CA_INCOME_BAND_OPTIONS) },
    // 2026-10-06 (Victor's deck-template rulings): the committee deck prints
    // "Monthly Income: $X", so the form asks for the monthly dollar amount
    // again. The 08-19 exact-USD field is REVIVED under its own key (its
    // stored answers and CSV column 35 are this same question), not minted
    // anew; scripts/publish-monthly-income-2228.mjs revives it on the live
    // form. Required for new applications — a (?) default
    // ('one-pager-income-fallback') — and the first half of the one-pager's
    // composite Monthly Income line.
    // #2523 — asked in CAD for CA; the key keeps its `_usd` name (stored data).
    { key: 'monthly_income_usd', label: 'What is your total monthly household income? (USD)', type: 'number', required: true, min: 0, committee_one_pager: true,
      conditionalLabel: canadaLabel('What is your total monthly household income? (CAD)') },
    // R21: deck default (see `hospital` above for the rule).
    { key: 'support_received', label: 'Do you currently receive any of the following support?', type: 'multi', required: true, helper: 'Select all that apply', options: SUPPORT_RECEIVED_OPTIONS, committee_one_pager: true, committee_label: 'Other Grants & Support', conditionalOptions: {
      on: 'country',
      onSection: 'intake',
      onLabel: 'Region',
      byValue: { US: SUPPORT_RECEIVED_OPTIONS, CA: CA_SUPPORT_RECEIVED_OPTIONS },
    } },
    { key: 'employment_impact', label: 'What was the employment impact due to your child’s diagnosis?', type: 'multi', required: true, helper: 'Select all that apply', options: EMPLOYMENT_IMPACT_OPTIONS, conditionalOptions: {
      on: 'country',
      onSection: 'intake',
      onLabel: 'Region',
      byValue: { US: EMPLOYMENT_IMPACT_OPTIONS, CA: CA_EMPLOYMENT_IMPACT_OPTIONS },
    } },
    { key: 'caregiver_time_off', label: 'Has the primary financial supporter had to take time off work due to your child’s diagnosis?', type: 'checkbox' },
    { key: 'time_off_who', label: 'If yes, who took time off work and for how long?', type: 'select', display: 'dropdown', options: TIME_OFF_WHO_OPTIONS },
    { key: 'stability_before', label: 'How financially stable did your household feel before your child’s diagnosis?', type: 'select', required: true, options: STABILITY_OPTIONS },
    { key: 'stability_current', label: 'How financially stable does your household feel currently?', type: 'select', required: true, options: STABILITY_OPTIONS },
    { key: 'biggest_stressor', label: 'What is the biggest financial stressor your family is currently facing?', type: 'select', required: true, display: 'dropdown', options: STRESSOR_OPTIONS },
    // 2027 monthly expense grid (replaces the single medical-only number,
    // retired below). expenses.other optional; the rest required.
    // #2523 — the expense grid reads CA$ for CA.
    { key: 'expenses.rent', label: 'Rent or mortgage ($ per month)', type: 'number', required: true, min: 0, groupLabel: 'Approximate monthly household expenses',
      conditionalLabel: canadaLabel('Rent or mortgage (CA$ per month)') },
    { key: 'expenses.utilities', label: 'Utilities ($ per month)', type: 'number', required: true, min: 0,
      conditionalLabel: canadaLabel('Utilities (CA$ per month)') },
    { key: 'expenses.transportation', label: 'Transportation ($ per month)', type: 'number', required: true, min: 0,
      conditionalLabel: canadaLabel('Transportation (CA$ per month)') },
    { key: 'expenses.food', label: 'Food ($ per month)', type: 'number', required: true, min: 0,
      conditionalLabel: canadaLabel('Food (CA$ per month)') },
    { key: 'expenses.childcare', label: 'Childcare ($ per month)', type: 'number', required: true, min: 0,
      conditionalLabel: canadaLabel('Childcare (CA$ per month)') },
    // R21: deck default (see `hospital` above for the rule).
    { key: 'expenses.medical', label: 'Medical out-of-pocket costs ($ per month)', type: 'number', required: true, min: 0, committee_one_pager: true, committee_label: 'Monthly Expenses Treatment',
      conditionalLabel: canadaLabel('Medical out-of-pocket costs (CA$ per month)') },
    { key: 'expenses.other', label: 'Other ($ per month)', type: 'number', min: 0,
      conditionalLabel: canadaLabel('Other (CA$ per month)') },
    { key: 'additional_challenges', label: 'What additional financial challenges is your family currently facing due to your child’s diagnosis?', type: 'textarea' },
    // 2027 Grant Prioritization section.
    // R21: deck default (see `hospital` above for the rule).
    { key: 'grant_priorities', label: 'If awarded TRF financial assistance, which expenses would you like the grant to prioritize?', type: 'multi', required: true, helper: 'Select all that apply', options: GRANT_PRIORITY_OPTIONS, committee_one_pager: true, committee_label: 'Where help matters most' },
    { key: 'assistance_impact', label: 'How would receiving financial assistance impact your family right now?', type: 'multi', required: true, helper: 'Select all that apply', options: ASSISTANCE_IMPACT_OPTIONS },
    // Live-only — kept pending #961 (free-text detail behind the structured
    // insurance profile now captured on the patient section).
    { key: 'insurance_notes', label: 'Additional insurance details', type: 'textarea' },
  ],
  travel: [
    { key: 'needed', label: 'Do you need to travel for your child’s treatment?', type: 'checkbox' },
    // #2523 — out-of-province, kilometres and Canadian dollars for CA.
    { key: 'travel_type', label: 'What type of travel is required for your child’s treatment?', type: 'multi', required: true, helper: 'Select all that apply', options: TRAVEL_TYPE_OPTIONS,
      conditionalOptions: canadaOptions(TRAVEL_TYPE_OPTIONS, CA_TRAVEL_TYPE_OPTIONS) },
    // R21: deck default (see `hospital` above for the rule).
    { key: 'miles_band', label: 'How many miles do you travel round trip per month for treatment?', type: 'select', required: true, display: 'dropdown', options: MILES_BAND_OPTIONS, committee_one_pager: true, committee_label: 'Roundtrip Miles Travel for Treatment',
      conditionalLabel: canadaLabel('How many kilometres do you travel round trip per month for treatment?'),
      conditionalOptions: canadaOptions(MILES_BAND_OPTIONS, CA_KM_BAND_OPTIONS) },
    { key: 'spend_band', label: 'On average, how much do you spend monthly on travel-related expenses (gas, flights, lodging, parking, etc.)?', type: 'select', required: true, display: 'dropdown', options: TRAVEL_SPEND_BAND_OPTIONS,
      conditionalOptions: canadaOptions(TRAVEL_SPEND_BAND_OPTIONS, CA_TRAVEL_SPEND_BAND_OPTIONS) },
    { key: 'relocated', label: 'Have you had to relocate temporarily or permanently for your child’s treatment?', type: 'select', required: true, options: RELOCATED_OPTIONS },
    { key: 'missed_treatment', label: 'Has your child ever missed or delayed treatment due to transportation, lodging, or financial barriers?', type: 'select', required: true, options: YES_NO },
    { key: 'burden_expenses', label: 'Which travel-related expenses have created the greatest financial burden?', type: 'multi', required: true, helper: 'Select all that apply', options: BURDEN_EXPENSE_OPTIONS },
    { key: 'overnight_stays', label: 'Does your child require overnight stays near the hospital for treatment?', type: 'select', required: true, options: OVERNIGHT_STAYS_OPTIONS },
    { key: 'stays_6mo', label: 'If yes, approximately how many hospital stays has your child had in the last 6 months?', type: 'select', display: 'dropdown', options: STAYS_6MO_OPTIONS },
    { key: 'stay_length', label: 'On average, how long are hospital stays?', type: 'select', required: true, display: 'dropdown', options: STAY_LENGTH_OPTIONS },
    { key: 'extended_away', label: 'Have you had to stay away from your home for extended periods due to your child’s treatment?', type: 'select', required: true, display: 'dropdown', options: EXTENDED_AWAY_OPTIONS },
    // Live-only — kept pending #961 (asks needs, not travel type — different
    // question from travel_type above).
    { key: 'out_of_state_needs', label: 'Out-of-state travel needs', type: 'multi', conditionalLabel: canadaLabel('Out-of-province travel needs'), options: [
      { value: 'car_rental', label: 'Car rental' },
      { value: 'airline_travel', label: 'Airline travel' },
      { value: 'lodging', label: 'Lodging' },
      { value: 'meal_expenses', label: 'Meal expenses' },
    ] },
  ],
};

// ── Retired fields (2027 match, D-FB-10) ──────────────────────────────────────
// Removed from the wizard; stored answers must keep rendering on staff surfaces
// (FamilyIntakeDrawer / ApplicationDetailPage render these read-only when a
// value exists) and their CSV columns are retained by the export contract.
export const RETIRED_SECTION_FIELDS: Record<SectionKey, FieldSpec[]> = {
  intake: [],
  patient: [
    // #1613 split this into `patient.risk_classification` and `patient.stage`,
    // published as application v5 on 2026-09-05. Retired rather than deleted:
    // families answered it, staff surfaces still render the stored value, and
    // its CSV column stays (#1704).
    { key: 'stage_risk', label: 'What is your child’s current stage or risk classification? (retired)', type: 'select', options: STAGE_RISK_OPTIONS },
  ],
  household: [
    { key: 'mailing_address', label: 'Primary mailing address (retired)', type: 'textarea' },
  ],
  finances: [
    { key: 'annual_income_usd', label: 'Annual household income (USD) (retired)', type: 'number' },
    { key: 'has_supplemental_income', label: 'Receives SS / welfare / supplemental income? (retired)', type: 'checkbox' },
    { key: 'time_off_detail', label: 'If yes, who took time off and how much? (retired)', type: 'textarea' },
    { key: 'monthly_oop_usd', label: 'Monthly out-of-pocket medical costs (USD) (retired)', type: 'number' },
  ],
  travel: [
    { key: 'monthly_miles', label: 'Approximate monthly miles to treatment (retired)', type: 'number' },
    { key: 'monthly_spend_bucket', label: 'Monthly travel spend (retired)', type: 'select', options: SPEND_BUCKETS },
  ],
};

export const APPLICATION_SECTIONS = ['intake', 'patient', 'household', 'finances', 'travel'] as const;

// ── Wizard screens (2027 workbook structure) ──────────────────────────────────
// The /apply wizard renders these SCREENS — the workbook's own section layout —
// while storage stays on the five APPLICATION_SECTIONS above (Family parity +
// admission are keyed on them; spec D-FB-9). A screen may reference fields from
// more than one storage section; advancing saves each touched section.
// PHI consent (code-composed step in ApplicationWizard) sits BEFORE the first
// patient-data screen, exactly as it sat before the patient section previously.

export interface ScreenFieldRef {
  section: SectionKey;
  key: string;
}

export interface ApplicationScreen {
  id: string;
  /** Short nav label (WizardProgress). */
  label: string;
  eyebrow: string;
  /** Serif screen title. */
  title: string;
  groups: { label?: string; fields: ScreenFieldRef[] }[];
}

export const APPLICATION_SCREENS: ApplicationScreen[] = [
  {
    id: 'region_cycle',
    label: 'Getting started',
    eyebrow: 'Let’s get started',
    title: 'Your application.',
    groups: [
      {
        fields: [
          { section: 'intake', key: 'application_type' },
          { section: 'intake', key: 'country' },
          { section: 'intake', key: 'family_last_name' },
        ],
      },
    ],
  },
  {
    id: 'patient_info',
    label: 'About your child',
    eyebrow: 'Patient information',
    title: 'Tell us about your child.',
    groups: [
      {
        fields: [
          { section: 'patient', key: 'first_name' },
          { section: 'patient', key: 'last_name' },
          { section: 'patient', key: 'date_of_birth' },
          { section: 'patient', key: 'gender' },
          { section: 'patient', key: 'gender_other' },
          { section: 'patient', key: 'ethnicity' },
          { section: 'patient', key: 'diagnosis' },
          { section: 'patient', key: 'diagnosed_at' },
          { section: 'patient', key: 'hospital' },
          { section: 'patient', key: 'school_status' },
        ],
      },
      {
        label: 'A few more questions',
        fields: [
          { section: 'patient', key: 'referring_social_worker' },
          { section: 'patient', key: 'treatment_location' },
          { section: 'intake', key: 'family_story' },
        ],
      },
    ],
  },
  {
    id: 'medical_insurance',
    label: 'Medical & insurance',
    eyebrow: 'Patient medical & insurance profile',
    title: 'Your child’s treatment and coverage.',
    groups: [
      {
        fields: [
          { section: 'patient', key: 'cancer_type' },
          { section: 'patient', key: 'cancer_type_other' },
          // #1613 / #1704 — the two halves of the retired `stage_risk`, in the
          // slot it held. A screen must never reference a retired field.
          { section: 'patient', key: 'risk_classification' },
          { section: 'patient', key: 'stage' },
          { section: 'patient', key: 'is_relapse' },
          { section: 'patient', key: 'treatment_status' },
          { section: 'patient', key: 'treatment_status_other' },
          { section: 'patient', key: 'treatment_frequency' },
          { section: 'patient', key: 'treatment_setting' },
          { section: 'patient', key: 'insurance_coverage' },
          { section: 'patient', key: 'insurance_coverage_other' },
          { section: 'patient', key: 'insurance_provider' },
          { section: 'patient', key: 'insurance_provider_other' },
          { section: 'patient', key: 'costs_not_covered' },
          { section: 'patient', key: 'deductible_met' },
        ],
      },
      {
        label: 'A few more questions',
        fields: [
          { section: 'patient', key: 'treatments' },
          { section: 'patient', key: 'treatment_ended_at' },
          { section: 'finances', key: 'insurance_notes' },
        ],
      },
    ],
  },
  {
    id: 'contact',
    label: 'Contact & household',
    eyebrow: 'Contact information',
    title: 'How we reach your family.',
    groups: [
      {
        label: 'Primary contact',
        fields: [
          { section: 'household', key: 'mother_full_name' },
          { section: 'household', key: 'primary_contact.relationship' },
          { section: 'household', key: 'primary_contact.email' },
          { section: 'household', key: 'primary_contact.phone' },
        ],
      },
      {
        label: 'Home address',
        fields: [
          { section: 'household', key: 'address.street' },
          { section: 'household', key: 'address.apt' },
          { section: 'household', key: 'address.city' },
          { section: 'intake', key: 'residence_state' },
          { section: 'household', key: 'address.zip' },
          { section: 'household', key: 'primary_language' },
        ],
      },
      {
        label: 'Secondary contact',
        fields: [
          { section: 'household', key: 'father_full_name' },
          { section: 'household', key: 'secondary_contact.relationship' },
          { section: 'household', key: 'secondary_contact.phone' },
          { section: 'household', key: 'secondary_contact.email' },
          { section: 'household', key: 'secondary_contact.authorized' },
        ],
      },
      {
        label: 'Your household',
        fields: [
          { section: 'household', key: 'single_parent' },
          { section: 'household', key: 'size' },
          { section: 'household', key: 'siblings' },
          { section: 'household', key: 'military_service' },
          { section: 'household', key: 'members' },
          { section: 'household', key: 'child_lives_with' },
          { section: 'household', key: 'primary_parent_guardian_dob' },
          { section: 'household', key: 'secondary_parent_guardian_dob' },
        ],
      },
    ],
  },
  {
    id: 'financial',
    label: 'Finances',
    eyebrow: 'Financial questions',
    title: 'Your family’s finances.',
    groups: [
      {
        fields: [
          { section: 'finances', key: 'income_band' },
          { section: 'finances', key: 'monthly_income_usd' },
          { section: 'finances', key: 'support_received' },
          { section: 'finances', key: 'employment_impact' },
          { section: 'finances', key: 'caregiver_time_off' },
          { section: 'finances', key: 'time_off_who' },
          { section: 'finances', key: 'stability_before' },
          { section: 'finances', key: 'stability_current' },
          { section: 'finances', key: 'biggest_stressor' },
        ],
      },
      {
        label: 'Approximate monthly household expenses',
        fields: [
          { section: 'finances', key: 'expenses.rent' },
          { section: 'finances', key: 'expenses.utilities' },
          { section: 'finances', key: 'expenses.transportation' },
          { section: 'finances', key: 'expenses.food' },
          { section: 'finances', key: 'expenses.childcare' },
          { section: 'finances', key: 'expenses.medical' },
          { section: 'finances', key: 'expenses.other' },
        ],
      },
      {
        fields: [
          { section: 'finances', key: 'additional_challenges' },
        ],
      },
    ],
  },
  {
    id: 'travel',
    label: 'Travel',
    eyebrow: 'Travel & transportation',
    title: 'Travel to treatment.',
    groups: [
      {
        fields: [
          { section: 'travel', key: 'needed' },
          { section: 'travel', key: 'travel_type' },
          { section: 'travel', key: 'miles_band' },
          { section: 'travel', key: 'spend_band' },
          { section: 'travel', key: 'relocated' },
          { section: 'travel', key: 'missed_treatment' },
          { section: 'travel', key: 'burden_expenses' },
          { section: 'travel', key: 'overnight_stays' },
          { section: 'travel', key: 'stays_6mo' },
          { section: 'travel', key: 'stay_length' },
          { section: 'travel', key: 'extended_away' },
        ],
      },
      {
        label: 'A few more questions',
        fields: [
          { section: 'travel', key: 'out_of_state_needs' },
        ],
      },
    ],
  },
  {
    id: 'prioritization',
    label: 'Grant priorities',
    eyebrow: 'Grant prioritization',
    title: 'Where the grant should help first.',
    groups: [
      {
        fields: [
          { section: 'finances', key: 'grant_priorities' },
          { section: 'finances', key: 'assistance_impact' },
        ],
      },
    ],
  },
  {
    id: 'mission_impact',
    label: 'Your story',
    eyebrow: 'TRF mission & impact',
    title: 'A little more about your family.',
    groups: [
      {
        fields: [
          { section: 'intake', key: 'how_heard' },
          { section: 'intake', key: 'immediate_impact' },
          { section: 'intake', key: 'without_assistance' },
        ],
      },
      {
        label: 'A few more questions',
        fields: [
          { section: 'intake', key: 'prior_hardship.flag' },
          { section: 'intake', key: 'prior_hardship.notes' },
        ],
      },
    ],
  },
];

/** Look up a FieldSpec (live or retired) by storage section + key. */
export function findFieldSpec(section: SectionKey, key: string): FieldSpec | undefined {
  return (
    SECTION_FIELDS[section].find((f) => f.key === key)
    ?? RETIRED_SECTION_FIELDS[section].find((f) => f.key === key)
  );
}

// PDF Q42 — the Signature Grant expense ask. NOT a Family-parity section (it
// becomes a GrantRequest on admission), so it lives OUTSIDE SECTION_FIELDS /
// APPLICATION_SECTIONS. The /apply wizard and Victor's renewal wizard both render it.
export const GRANT_REQUEST_FIELDS: FieldSpec[] = [
  // #1169 — travel was missing entirely, so a family on a Travel grant could not
  // ask for the one thing their grant is for; their only route was "Other".
  // The three rows mirror the initial application's travel block rather than
  // inventing a taxonomy. Whether the $1,000 cap should apply to a Travel family
  // at all is TRF's to answer — marked on screen as `renewal-grant-scope`.
  { key: 'expense_category', label: 'Which expense should the grant help with?', type: 'select', options: [
    // #2544: worded as the grants catalog words it (grantExpenseCategories.js).
    { value: 'rent_mortgage', label: 'Rent or mortgage' },
    { value: 'car_payment', label: 'Car payment' },
    { value: 'utilities', label: 'Utilities' },
    { value: 'travel_mileage', label: 'Travel — mileage / fuel' },
    { value: 'travel_lodging', label: 'Travel — lodging near treatment' },
    { value: 'travel_meals', label: 'Travel — meals while travelling' },
    { value: 'other', label: 'Other' },
  ] },
  // B-405: every other "other" in the intake has a specify companion
  // (gender_other, cancer_type_other, treatment_status_other,
  // insurance_coverage_other); this one did not, so a family whose need is not
  // rent/car/utilities had no way to say what it is. Woven into
  // GrantRequest.category_detail by backend/src/services/grantMaterialization.js
  // — do not add a grant_request field without checking it reaches staff there.
  { key: 'expense_category_other', label: 'If other, please specify', type: 'text' },
  // #2376: required because the wizard (#2213) and the server refuse a grant
  // ask with no amount; the flag is what draws the asterisk and aria-required.
  { key: 'amount_requested_usd', label: 'Amount requested (USD, up to $1,000)', type: 'number', min: 1, max: 1000, required: true },
  // Discrete creditor fields (replaced a single free-text "expense_detail" blob).
  // Serialized into GrantRequest.category_detail at materialization
  // (services/grantMaterialization.js). Shared by the /apply and renewal wizards.
  // AP-08 (do creditor details belong in an application?) is an open TRF
  // decision — fields kept as-is pending it.
  { key: 'expense_account_holder', label: 'Account holder', type: 'text' },
  { key: 'expense_creditor_name', label: 'Creditor name', type: 'text' },
  { key: 'expense_creditor_address', label: 'Creditor address', type: 'text' },
  { key: 'expense_account_number', label: 'Account number', type: 'text' },
  { key: 'expense_amount_per_bill', label: 'Amount per bill (USD)', type: 'number' },
];

export function get(obj: Record<string, unknown> | undefined, path: string): unknown {
  if (!obj) return undefined;
  return path.split('.').reduce<unknown>((acc, k) => {
    if (acc && typeof acc === 'object' && k in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[k];
    }
    return undefined;
  }, obj);
}

// #583 — resolve a field's display label against the other values in its
// section. Mirrors how every renderer already resolves `conditionalOptions`.
//
// #2523: a label driven from ANOTHER section (`conditionalLabel.onSection`)
// reads its driver from `sectionValues`, the whole answer set keyed by section
// id — the same resolver the option lists use, so the two can never disagree
// about which country a family is in.
export function resolveFieldLabel(
  field: FieldSpec,
  siblingValues?: Record<string, unknown>,
  sectionValues?: Record<string, Record<string, unknown> | undefined>,
): string {
  const conditional = field.conditionalLabel;
  if (!conditional) return field.label;
  const driverKey = resolveConditionalDriver(conditional, siblingValues, sectionValues);
  return conditional.byValue[driverKey] ?? field.label;
}

/**
 * #602 — the value that selects a `conditionalOptions` branch.
 *
 * Two driver shapes, one resolver, so no renderer has to know which it is
 * looking at. Without `onSection` the driver is a SIBLING within the field's own
 * section, which is what `intake.residence_state` reads and the only shape that
 * existed before #602. With `onSection` it lives in ANOTHER section — the
 * ethnicity taxonomy is driven by `intake.country` while the question itself is
 * a `patient` one — and is read out of `sectionValues`, the whole answer set
 * keyed by section id.
 *
 * A named section is never shadowed by a same-named sibling key: the driver has
 * one home, and falling back to a local `country` would resolve a different
 * question's answer. An unreachable or non-string driver resolves to '', which
 * indexes no branch — callers then disable (select) or fall back to the static
 * list (multi), exactly as they did before.
 */
const warnedMissingSectionValues = new Set<string>();

/**
 * Test seam — the warning below fires once per driver so a dev console is not
 * flooded on every re-render, which means a suite asserting on it has to clear
 * the memo between cases.
 */
export function __resetConditionalDriverWarnings(): void {
  warnedMissingSectionValues.clear();
}

export function resolveConditionalDriver(
  conditional: { on: string; onSection?: SectionKey } | undefined,
  siblingValues?: Record<string, unknown>,
  sectionValues?: Record<string, Record<string, unknown> | undefined>,
): string {
  if (!conditional) return '';
  // #602 — `sectionValues` is optional because a control must render before the
  // driver is answered, and the fallback to the static list is CORRECT for a
  // family who has not reached that question yet. It is a bug when a SURFACE
  // simply forgot the prop, and on screen the two are indistinguishable: a
  // Canadian family is served the US Census list, silently, with the type
  // checker satisfied. Distinguishable here, though — an absent `sectionValues`
  // is the surface, an empty one is the applicant — so development says so out
  // loud rather than leaving the omission to be found by a family.
  // #602 — a surface that forgets `sectionValues` silently serves the US list.
  if (conditional.onSection && sectionValues === undefined) {
    const driver = `${conditional.onSection}.${conditional.on}`;
    if (!warnedMissingSectionValues.has(driver)) {
      warnedMissingSectionValues.add(driver);
      if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
        console.warn(
          `[intake] a field whose options or wording are driven by "${driver}" rendered without \`sectionValues\`, `
          + 'so it is falling back to its static list and label. Pass sectionValues={<every section, '
          + 'keyed by section id>} from this surface — otherwise a Canadian family is served the US list.',
        );
      }
    }
  }
  const source = conditional.onSection
    ? sectionValues?.[conditional.onSection]
    : siblingValues;
  const driver = source ? get(source, conditional.on) : undefined;
  return typeof driver === 'string' ? driver : '';
}

/**
 * #2523 follow-up — the answers whose US and Canadian lists are the SAME
 * question in two currencies or units, band for band. Each pair is
 * `[US value, CA value]`, zipped by position from the lists above, which are
 * written in the same order (the CA lists are the US bands converted, or the
 * US list with one answer re-worded). Shared values (`not_applicable`,
 * `unsure`, `no`) are their own equivalent and are left out.
 *
 * Explicit by field rather than "any conditional select whose branches have
 * the same length": the ethnicity and insurance taxonomies (#602, #2399) are
 * different questions per country, and pairing them by position would put
 * words in a family's mouth.
 */
function zipBranchValues(
  us: ReadonlyArray<{ value: string }>,
  ca: ReadonlyArray<{ value: string }>,
): ReadonlyArray<readonly [string, string]> {
  if (us.length !== ca.length) throw new Error('country answer equivalents: the two lists must be band for band');
  return us.map((option, i) => [option.value, ca[i].value] as const).filter(([u, c]) => u !== c);
}

export const COUNTRY_ANSWER_EQUIVALENTS: ReadonlyArray<{
  section: SectionKey;
  key: string;
  pairs: ReadonlyArray<readonly [string, string]>;
}> = [
  { section: 'household', key: 'military_service', pairs: zipBranchValues(MILITARY_SERVICE_OPTIONS, CA_MILITARY_SERVICE_OPTIONS) },
  { section: 'travel', key: 'travel_type', pairs: zipBranchValues(TRAVEL_TYPE_OPTIONS, CA_TRAVEL_TYPE_OPTIONS) },
  { section: 'travel', key: 'miles_band', pairs: zipBranchValues(MILES_BAND_OPTIONS, CA_KM_BAND_OPTIONS) },
];

/**
 * Money bands are NOT carried (Victor, 2026-10-11, option a): "$25,000 – $49,999" and "CA$25,000 – CA$49,999" are the
 * same position but not the same amount, so relabelling would understate a family's income by about a quarter. An
 * answer on the other country's list is cleared from what the family sees and they choose again; the earlier answer
 * is returned so the wizard can name it. Distance (miles → km bands) is a real conversion and stays in the table above.
 */
export const COUNTRY_REPICK_FIELDS: ReadonlyArray<{
  section: SectionKey;
  key: string;
  us: ReadonlyArray<{ value: string; label: string }>;
  ca: ReadonlyArray<{ value: string; label: string }>;
}> = [
  { section: 'finances', key: 'income_band', us: INCOME_BAND_OPTIONS, ca: CA_INCOME_BAND_OPTIONS },
  { section: 'travel', key: 'spend_band', us: TRAVEL_SPEND_BAND_OPTIONS, ca: CA_TRAVEL_SPEND_BAND_OPTIONS },
];

/**
 * #2610 — answers whose US and Canadian lists are DIFFERENT questions (a US
 * state is not a province; Medicaid is not a supplemental plan), so an answer
 * from the other country's list is neither carried nor named: it is cleared,
 * saved cleared, and the required gate asks the family afresh. Without this the
 * select kept "Nevada" / "Medicaid" as an out-of-list passthrough pill (#1570)
 * inside the Canadian list, pre-selected.
 */
export const COUNTRY_CLEAR_FIELDS: ReadonlyArray<{ section: SectionKey; key: string }> = [
  { section: 'intake', key: 'residence_state' },
  { section: 'patient', key: 'insurance_coverage' },
  { section: 'patient', key: 'insurance_provider' },
];

/** #2610 — a postal code written for the other country (a five-digit US ZIP
 *  under a Canadian address, or an `A1A 1A1` code under a US one). */
const US_ZIP = /^\d{5}(-\d{4})?$/;
const CA_POSTAL = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;
export function postalCodeFromOtherCountry(value: unknown, country: 'US' | 'CA'): boolean {
  if (typeof value !== 'string') return false;
  const v = value.trim();
  return country === 'CA' ? US_ZIP.test(v) : CA_POSTAL.test(v);
}

/** One answer the family must choose again, with the earlier answer's value and the list it came from.
 *  `previousLabel` (#2611) is that answer's label as the CURRENT published form words it — translated when
 *  the form renders in the family's language — read off the other country's branch at alignment time. */
export interface CountryRepick { section: SectionKey; key: string; previous: string; previousCountry: 'US' | 'CA'; previousLabel?: string }

/**
 * #2523 follow-up — carry answers given on the OTHER country's list onto the
 * family's current country, band for band.
 *
 * Why: a renewal is pre-filled from the Family record (`createRenewal` copies
 * the sections verbatim), and a Canadian family admitted before #2523 — or
 * seeded by `seed-e2e-2305.mjs` — holds US values (`25k_49k`, `200_499`). The
 * Canadian list does not carry them, so the select kept each as an
 * out-of-list passthrough (#1570) labelled from the static US `options`: a
 * kilometre question answered "200–499 miles", in every language. The same
 * happens on a first application when a family answers finances and then
 * changes Region.
 *
 * An answer moves only when the field's CURRENT branch (from `findField`, the
 * published definition) offers the equivalent — a definition not yet carrying
 * the Canadian lists keeps the stored answer, which is then on its list.
 * Returns the same `sections` object when nothing moves, plus the
 * `[section, key]` of each moved answer so the caller can save it.
 */
export function alignCountryAnswers<S extends Partial<Record<SectionKey, Record<string, unknown> | undefined>>>(
  sections: S,
  findField: (section: SectionKey, key: string) => FieldSpec | undefined,
): { sections: S; moved: Array<[SectionKey, string]>; repick: CountryRepick[] } {
  const country = sections.intake?.country;
  if (country !== 'CA' && country !== 'US') return { sections, moved: [], repick: [] };
  const toCa = country === 'CA';
  let next = sections;
  const moved: Array<[SectionKey, string]> = [];
  for (const { section, key, pairs } of COUNTRY_ANSWER_EQUIVALENTS) {
    const local = next[section];
    const value = local ? get(local, key) : undefined;
    if (value == null) continue;
    const field = findField(section, key);
    const offered = field?.conditionalOptions?.byValue[country];
    if (!offered) continue;
    const onOffer = new Set(offered.map((o) => o.value));
    const map = new Map(pairs.map(([us, ca]) => (toCa ? [us, ca] : [ca, us])));
    const carry = (v: unknown) => {
      if (typeof v !== 'string' || onOffer.has(v)) return v;
      const target = map.get(v);
      return target && onOffer.has(target) ? target : v;
    };
    let carried: unknown;
    if (Array.isArray(value)) {
      const out = value.map(carry);
      carried = out.some((v, i) => v !== value[i]) ? Array.from(new Set(out)) : value;
    } else {
      carried = carry(value);
    }
    if (carried === value) continue;
    next = { ...next, [section]: setPath(local ?? {}, key, carried) };
    moved.push([section, key]);
  }
  const repick: CountryRepick[] = [];
  for (const { section, key, us, ca } of COUNTRY_REPICK_FIELDS) {
    const local = next[section];
    const value = local ? get(local, key) : undefined;
    if (typeof value !== 'string' || value === '') continue;
    const field = findField(section, key);
    const offered = field?.conditionalOptions?.byValue[country];
    if (!offered || offered.some((o) => o.value === value)) continue;
    const other = toCa ? us : ca;
    if (!other.some((o) => o.value === value)) continue; // a retired or unknown value keeps the #1570 passthrough
    // Cleared on screen only; the caller does NOT mark it dirty, so the server keeps the earlier answer until the
    // family picks a band.
    next = { ...next, [section]: setPath(local ?? {}, key, '') };
    const previousCountry = toCa ? 'US' : 'CA';
    // #2611: the note names the earlier band in the family's language — the published form's own (translated)
    // label for it on the other country's branch, not the static English list.
    const previousLabel = field?.conditionalOptions?.byValue[previousCountry]?.find((o) => o.value === value)?.label;
    repick.push({ section, key, previous: value, previousCountry, ...(previousLabel ? { previousLabel } : {}) });
  }
  // #2610: a US state, a US insurance plan or carrier under a Canadian family (and the reverse) is cleared outright
  // — there is no equivalent to carry and nothing worth naming — and reported as moved, so the caller saves the
  // cleared value and the required gate asks again on the step itself.
  for (const { section, key } of COUNTRY_CLEAR_FIELDS) {
    const local = next[section];
    const value = local ? get(local, key) : undefined;
    if (typeof value !== 'string' || value === '') continue;
    const byValue = findField(section, key)?.conditionalOptions?.byValue;
    const offered = byValue?.[country];
    if (!offered || offered.some((o) => o.value === value)) continue;
    const other = byValue?.[toCa ? 'US' : 'CA'];
    if (!other?.some((o) => o.value === value)) continue; // a retired or unknown value keeps the #1570 passthrough
    next = { ...next, [section]: setPath(local ?? {}, key, '') };
    moved.push([section, key]);
  }
  // #2610: the postal code written for the other country goes with the state / province it belonged to.
  const zip = next.household ? get(next.household, 'address.zip') : undefined;
  if (postalCodeFromOtherCountry(zip, country)) {
    next = { ...next, household: setPath(next.household ?? {}, 'address.zip', '') };
    moved.push(['household', 'address.zip']);
  }
  return { sections: next, moved, repick };
}

/** The label of a re-pick's earlier answer: the published form's own words for it (`previousLabel`, translated
 *  with the form, #2611), else the static list it was given on (English). */
export function repickPreviousLabel(r: CountryRepick): string {
  if (r.previousLabel) return r.previousLabel;
  const entry = COUNTRY_REPICK_FIELDS.find((f) => f.section === r.section && f.key === r.key);
  const list = r.previousCountry === 'US' ? entry?.us : entry?.ca;
  return list?.find((o) => o.value === r.previous)?.label ?? r.previous;
}

export function setPath(obj: Record<string, unknown>, path: string, value: unknown): Record<string, unknown> {
  const keys = path.split('.');
  const result = { ...obj };
  let cursor: Record<string, unknown> = result;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    const existing = cursor[k];
    const next = (existing && typeof existing === 'object' && !Array.isArray(existing))
      ? { ...(existing as Record<string, unknown>) } : {};
    cursor[k] = next;
    cursor = next;
  }
  cursor[keys[keys.length - 1]] = value;
  return result;
}

export function fmtDateForInput(v: unknown): string {
  if (!v) return '';
  if (typeof v === 'string') {
    const d = new Date(v);
    if (isNaN(d.getTime())) return '';
    const iso = d.toISOString().slice(0, 10);
    // #2448: a year past 9999 serialises as `+020126-10-08…`, and its first ten
    // characters are not a date at all.
    return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : '';
  }
  return '';
}

/**
 * A stored date rendered for READING — the display counterpart of
 * `fmtDateForInput` above, and it has to agree with it.
 *
 * A bare locale-date call on `new Date('2019-04-02')` reads the string as UTC
 * midnight and then converts to the reader's zone, so every staff member west
 * of UTC sees the PREVIOUS DAY. TRF is Pacific and Nevada-based; the whole team
 * reads every date one day early (B-432).
 *
 * That was merely wrong while these fields were read-only. Once they became
 * editable it became dangerous: the row says "4/1/2019", the date input opened
 * from that row says "2019-04-02", and a staff member reconciling the two
 * writes a wrong date of birth into a child's record.
 *
 * A date-only value denotes a DAY, not an instant, so it is formatted in the
 * zone it was stored in. Values carrying a real time-of-day are left alone.
 */
export function fmtDateForDisplay(v: unknown, locale?: string): string {
  if (typeof v !== 'string' || !v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  return formatPreset(d, 'numeric', { locale, dateOnly: true });
}

/**
 * #2704: answer paths whose date-typed field holds an event INSTANT, not a
 * calendar day. `intake.applied_at` is stamped with the submit moment
 * (backend FamilyApplication.js), so its UTC day put an evening submission on
 * the next day. Mirrors `INSTANT_ANSWER_PATHS` in backend/src/services/dateAnswers.js.
 */
export const INSTANT_DATE_PATHS: ReadonlySet<string> = new Set(['intake.applied_at']);

/**
 * A date-only string, or UTC midnight: a calendar day (typed, imported, or an
 * application date staff entered by hand), which keeps the day it names.
 */
export function isCalendarDayValue(v: string): boolean {
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return true;
  const d = new Date(v);
  return !isNaN(d.getTime())
    && d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0;
}

/**
 * #2704: a stored date answer at `path` that is an event INSTANT, as the day it
 * happened in the viewer's timezone (`trfEventDate`, "Oct 11, 2026"): the day
 * the application page header names for the same submit. Null for everything
 * else (a calendar day, any other path, a non-date field), which the caller
 * prints through `formatFieldValue` as the day typed.
 */
export function instantAnswerDay(path: string, value: unknown, field: Pick<FieldSpec, 'type'>): string | null {
  if (field.type !== 'date' || !INSTANT_DATE_PATHS.has(path) || typeof value !== 'string') return null;
  if (!/^\d{4}-\d{2}-\d{2}T/.test(value) || isCalendarDayValue(value)) return null;
  return trfEventDate(value);
}

export function asArray(v: unknown): string[] {
  return Array.isArray(v) ? (v as string[]) : [];
}

// Named format validators (QA fix wave 2026-08-20). Deliberately LOOSE — the
// point is catching "a" / "not-an-email", not policing real-world variety
// (Canadian postal codes, international phones). Names are what the future
// form-definition model serializes.
export const FIELD_FORMATS: Record<NonNullable<FieldSpec['format']>, { test: (v: string) => boolean; message: string }> = {
  email: {
    test: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()),
    message: 'Enter a valid email address.',
  },
  phone: {
    test: (v) => v.replace(/\D/g, '').length >= 7,
    message: 'Enter a valid phone number.',
  },
  zip: {
    test: (v) => /^[A-Za-z0-9][A-Za-z0-9\s-]{2,9}$/.test(v.trim()),
    message: 'Enter a valid ZIP / postal code.',
  },
};

/**
 * #2441 — the years a typed date may carry. A date of birth or diagnosis date
 * outside this window is a typo (Safari's date input saved "12" as 0012), not
 * a fact. Mirrored by `backend/src/services/dateAnswers.js`.
 */
export const MEMORABLE_YEAR_MIN = 1900;
export const MEMORABLE_YEAR_MAX = 2100;

/** True when a stored date answer is a real calendar day, YYYY-MM-DD, inside
 *  the year window. */
export function isPlausibleDateAnswer(value: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (y < MEMORABLE_YEAR_MIN || y > MEMORABLE_YEAR_MAX) return false;
  const probe = new Date(Date.UTC(y, mo - 1, d));
  return probe.getUTCFullYear() === y && probe.getUTCMonth() === mo - 1 && probe.getUTCDate() === d;
}

/** Range/format problem for a NON-EMPTY value; null when the value is fine.
 *  Empty values are the required gate's business (isFieldValueEmpty), not ours. */
export function fieldValueProblem(field: FieldSpec, value: unknown): string | null {
  if (isFieldValueEmpty(field, value)) return null;
  if (field.type === 'number' && typeof value === 'number') {
    const { min, max } = field;
    if (min != null && max != null && (value < min || value > max)) return `Enter a value between ${min} and ${max}.`;
    if (min != null && value < min) return `Enter ${min} or more.`;
    if (max != null && value > max) return `Enter ${max} or less.`;
  }
  if (field.type === 'date' && typeof value === 'string' && !isPlausibleDateAnswer(value)) {
    return 'Enter a valid date.';
  }
  if (field.format && typeof value === 'string') {
    const fmt = FIELD_FORMATS[field.format];
    if (!fmt.test(value)) return fmt.message;
  }
  return null;
}

/** True when a field has no usable answer for required-field validation.
 *  Checkboxes are never "empty" (unchecked is a valid No — see the PR-1
 *  deviations note: explicit Yes/No questions use selects, not checkboxes).
 *  Number 0 is a real answer; only null/undefined/'' count as empty. */
export function isFieldValueEmpty(field: Pick<FieldSpec, 'type'>, value: unknown): boolean {
  if (field.type === 'checkbox') return false;
  // D-FB-12, twin of the server's `isEmptyAnswer` file arm: only the document
  // id decides. Without this the default branch below returns false for any
  // non-string, so a required file question could never block step-advance.
  if (field.type === 'file') return !(value as FileAnswer | null)?.source_document_id;
  if (field.type === 'multi') return asArray(value).length === 0;
  if (field.type === 'number') return value === null || value === undefined || value === '';
  return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
}

/**
 * #1219 — whether a `conditionalOptions` field has an option list to offer
 * RIGHT NOW. The answerability twin of `isFieldValueEmpty`: that one asks
 * whether the family answered, this one asks whether they *could*.
 *
 * Client port of the server's `hasAnythingToPick`
 * (`backend/src/services/publishedForms.js`, shipped in #1214 / B-458), and
 * pinned to it by `backend/tests/contract/wizard-required-gate-parity.test.js`.
 *
 * Mirrors the renderer exactly (`IntakeFieldInput.tsx:156-173`): the driver is
 * a SIBLING within the same section, a non-string answer indexes nothing, and
 * an empty branch renders a DISABLED control — so demanding it would strand
 * the family on a control they cannot operate.
 *
 * Two guards keep the demand in place, both returning `true`:
 *  - a field carrying live static options of its own is answerable whatever
 *    the driver says, so it never reaches the branch lookup (fail CLOSED);
 *  - a driver missing from the published spec keeps the demand rather than
 *    silently suppressing it (fail OPEN, the posture `isRevealed` takes).
 *
 * Note the deliberate shape difference from the server: there, `options` mixes
 * live and retired entries and needs `.some((o) => !o.retired)`. Here `options`
 * IS the live set — retired option labels live in `optionLabels` (#1011) — so
 * a length check is the exact analogue, not a weakening of it.
 *
 * A stored legacy value needs no handling: it is non-empty, so
 * `isFieldValueEmpty` short-circuits before this is ever consulted.
 */
export function hasAnythingToPick(
  field: FieldSpec,
  siblingValues: Record<string, unknown> | undefined,
  findSibling?: (key: string) => FieldSpec | undefined,
): boolean {
  const conditional = field.conditionalOptions;
  if (!conditional) return true;
  if ((field.options ?? []).length > 0) return true;
  if (findSibling && !findSibling(conditional.on)) return true;
  const driver = siblingValues ? get(siblingValues, conditional.on) : undefined;
  const branch = typeof driver === 'string' ? conditional.byValue[driver] : undefined;
  return (branch ?? []).length > 0;
}

/**
 * The impossible-date rules moved to `lib/dateRules.ts` in #1838.
 *
 * #1111 shipped them here as three rules hardcoded on two seeded field ids,
 * with a note that `FieldSpec` expressed no date bound and no field-to-field
 * comparison. #1838 added both (`dateBound` / `dateCompare` above) and one
 * evaluator for them, and this module is deliberately IMPORT-FREE — the seed
 * generator bundles it — so it cannot call that evaluator. The rules are data
 * in `dateRules.ts` now (`SEEDED_DATE_RULES`), running through the same code
 * path a builder-authored rule does.
 *
 * `import { impossibleDateProblems } from './dateRules'`.
 */

/** #2462: a choice question with more options than this renders as a
 *  dropdown, not a row of chips — past three, the chips wrap into a wall that
 *  reads as several questions (Victor, 2026-10-09). */
export const CHIP_OPTION_LIMIT = 3;

/** The native <select> look, shared with the pre-screen's long questions.
 *  #2760: no vertical padding. iOS WebKit lays a select's text out from the
 *  top padding down and clips what the fixed h-10 box can't hold, so `py-2`
 *  cut the descenders ("English" read "Enalish"); every engine centres the
 *  text in the height on its own. `nativeSelectClip.test.ts` holds the class. */
export const NATIVE_SELECT_CLASS = 'flex h-10 w-full rounded-md border border-input bg-background px-3 py-0 text-body ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';
